import { Suspense, lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Navigate, useRoutes, useLocation, useParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
// layouts
import MainLayout, { OrgLayout, KioskLayout, UserLayout } from '../layouts/main';
import AtlasLayout from '../layouts/atlas/AtlasLayout';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import UserAuthGuard from '../guards/UserAuthGuard';
import AdminAuthGuard from '../guards/AdminAuthGuard';
import KioskAuthGuard from '../guards/KioskAuthGuard';
import BrokerGuard from '../guards/BrokerGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import WedgeHomeRedirect from '../guards/WedgeHomeRedirect';
import AccessProviderGuard from '../guards/AccessProviderGuard';
// config
// components
import LoadingScreen from '../components/LoadingScreen';
import RouteErrorBoundary from '../components/RouteErrorBoundary';
import OutreachLayout from '../pages/admin/emailMarketing/OutreachLayout';
import usePermission from '../hooks/usePermission';
import { PATH_ADMIN } from './paths';

// ----------------------------------------------------------------------

// Boss sub-roles that belong to the boss business system (orgs, orders,
// billing, CRM). Everything except `publisher`, which is a deskos.net content
// role and must not inherit the rest of the console just for being a boss
// account. Mirrors CONTENT_ONLY_BOSS_ROLES in accounts/boss/scoping.py, which
// is where it is actually enforced.
const BOSS_STAFF_ROLES = ['super_admin', 'manager', 'relationship_manager'];

// Sprints, stories and deploy requests. Two build-only sub-roles plus the
// founder; a `manager` or `relationship_manager` has no business on the board,
// and a `developer` has none in billing. Mirrors BUILD_ROLES in
// deskos_billing/sprints/permissions.py.
const BUILD_ROLES = ['super_admin', 'developer', 'product_manager'];

// DeskOS training requests: every boss sub-role except developer. Changing what
// we sell and when we work is super_admin only. Mirrors is_training_staff in
// deskos_billing/training/views.py.
const TRAINING_ROLES = ['super_admin', 'manager', 'relationship_manager', 'publisher', 'product_manager'];

// Where /admin/expenses lands depends on what you are allowed to see. The
// Control Center is money end to end, so anyone without financials.view opens
// on the ledger instead of bouncing off a 403.
function ExpensesHome() {
  const canSeeMoney = usePermission('financials.view');
  return <Navigate to={canSeeMoney ? '/admin/expenses/dashboard' : '/admin/expenses/all'} replace />;
}

// The boundary sits outside Suspense on purpose: a lazy import that rejects
// throws during render, and only a boundary above it can catch that. Without
// one, a chunk the deploy replaced took down the whole console instead of just
// the page that needed it.
/**
 * Keeps a "rooms" spelling of a meeting-room URL working.
 *
 * The screen is called Rooms now, so /admin/rooms is the address people type
 * and the one a colleague passes on. Every meeting-room URL stays exactly
 * where it is: public booking links already printed on a door, directory
 * links already emailed and every admin bookmark resolve as they always did,
 * and the rooms spelling lands on the same page through here.
 *
 * A redirect rather than a second mount, so there is one page with one
 * address and no two copies of it to keep in step. The query string and hash
 * come along because a booking link carries its date in them, and so does the
 * location state, which is how the member flow passes a room to checkout.
 */
const RoomsAlias = ({ to }) => {
  const params = useParams();
  const { search, hash, state } = useLocation();
  const target = to.replace(/:(\w+)/g, (_, name) => params[name] ?? '');
  const query = target.includes('?') && search ? `&${search.slice(1)}` : search;
  return <Navigate to={`${target}${query}${hash}`} state={state} replace />;
};

RoomsAlias.propTypes = {
  to: PropTypes.string.isRequired,
};

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <RouteErrorBoundary key={pathname}>
      <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/admin/dashboard')} />}>
        <Component {...props} />
      </Suspense>
    </RouteErrorBoundary>
  );
};

const UserLoadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <RouteErrorBoundary key={pathname}>
      <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/user/dashboard')} />}>
        <Component {...props} />
      </Suspense>
    </RouteErrorBoundary>
  );
};

// eslint-disable-next-line import/no-unresolved, import/extensions
const ChatWidgetPage = Loadable(lazy(() => import('../pages/widget/ChatWidgetPage')));
const PublicCafeteriaLanding = Loadable(lazy(() => import('../pages/public/PublicCafeteriaLanding')));
const PublicCampusCafeterias = Loadable(lazy(() => import('../pages/public/PublicCampusCafeterias')));
const ShortLinkRedirect = Loadable(lazy(() => import('../pages/public/ShortLinkRedirect')));
const LegalDocumentPage = Loadable(lazy(() => import('../pages/public/LegalDocumentPage')));
const StripeReturn = Loadable(lazy(() => import('../pages/user/orders/StripeReturn')));

export default function Router() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  return useRoutes([
    {
      path: 'user',
      element: (
        <UserAuthGuard>
          <UserLayout />
        </UserAuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserSettings />
            </RoleBasedGuard>
          ),
        },
        {
          // Member door list for providers that unlock over the API (Kisi).
          // Spintly unlocks over BLE inside the native SDK, so the Flutter app
          // shows its own screen and never routes here.
          path: 'doors',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <MemberDoorsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'feed',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="community">
              <UserFeed />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'feed/post/:id',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="community">
              <PostDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="events">
              <UserEventsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/:id',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="events">
              <UserEventDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'profile',
          element: (
            <RoleBasedGuard roles={['user']}>
              <UserProfile />
            </RoleBasedGuard>
          ),
        },
        // Unified plans Phase 3: /user/plans is the one storefront.
        { path: 'space-pricing', element: <RoomsAlias to="/user/plans" /> },
        {
          path: 'seat-selector',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="user-plans">
              <SeatSelectorPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'service-requests',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="helpdesk">
              <UserServiceRequests />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'service-requests/new',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="helpdesk">
              <UserServiceRequestForm />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'service-requests/:id',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="helpdesk">
              <UserServiceRequestDetail />
            </RoleBasedGuard>
          ),
        },
        {
          // Gated on the `courier` plan feature, the same slug the member nav
          // entry uses, so a member of an org without the module cannot reach
          // the page by typing the URL.
          path: 'courier',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="courier">
              <MyCourier />
            </RoleBasedGuard>
          ),
        },
        {
          // No plan-feature slug and no permission: refreshments is a campus
          // switch, not a module a member buys. The page itself asks the
          // server whether this campus serves desks and says so when it does
          // not, which is the same answer a URL typed by hand deserves.
          path: 'refreshments',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <MyRefreshments />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'printing',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserPrintingHome />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'printing/checkout/',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserPrintingCheckout />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'printing/new',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserPrintingNew />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'printing/jobs/:jobId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserPrintingDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'my-visits',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="visits">
              <MyVisits />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MeetingRoomUserList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room/room/:roomId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MeetingRoomUserDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room/book',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MeetingRoomUserBooking />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room/extend/:bookingId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MeetingRoomUserExtend />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room/my-bookings',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MyMeetingRoomBooking />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room/checkout',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MeetingRoomUserCheckout />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room/booking/success',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="meeting-room">
              <MeetingRoomBookingSuccess />
            </RoleBasedGuard>
          ),
        },
        // The Rooms spelling of the member screens, for the same reason as the
        // admin ones: the section is called Rooms now, so that is the address
        // people type. Redirects, not second mounts, and every
        // /user/meeting-room address stays canonical so anything a member has
        // bookmarked keeps working untouched.
        { path: 'rooms', element: <RoomsAlias to="/user/meeting-room" /> },
        { path: 'rooms/room/:roomId', element: <RoomsAlias to="/user/meeting-room/room/:roomId" /> },
        { path: 'rooms/book', element: <RoomsAlias to="/user/meeting-room/book" /> },
        { path: 'rooms/extend/:bookingId', element: <RoomsAlias to="/user/meeting-room/extend/:bookingId" /> },
        { path: 'rooms/my-bookings', element: <RoomsAlias to="/user/meeting-room/my-bookings" /> },
        { path: 'rooms/checkout', element: <RoomsAlias to="/user/meeting-room/checkout" /> },
        { path: 'rooms/booking/success', element: <RoomsAlias to="/user/meeting-room/booking/success" /> },
        { path: 'room-credits', element: <RoomsAlias to="/user/plans?type=credit-packs" /> },
        {
          path: 'plans',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <PlanStorePage />
            </RoleBasedGuard>
          ),
        },
        { path: 'meeting-room-credits', element: <RoomsAlias to="/user/plans?type=credit-packs" /> },
        {
          path: 'checkout',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <CheckoutPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'checkout/success',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <CheckoutSuccess />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'checkout/stripe-return',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <StripeReturn />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'my-orders',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }} slug="orders">
              <MyOrders />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'visitor-management',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }} slug="visitor-management">
              <UserVisitorManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'company',
          children: [
            {
              path: 'home',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }} slug="advanced-company-management">
                  <TeamManagementDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'attendance',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }} slug="advanced-company-management">
                  <AttendanceDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'attendance/:id',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }} slug="advanced-company-management">
                  <MemberAttendance />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'attendance/report',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }} slug="advanced-company-management">
                  <AttendanceReport />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }}>
                  <TeamMembers />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'billing',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }}>
                  <CompanyBilling />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['user']} allowed={{ user: ['admin'] }}>
                  <CompanySettings />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'mealos',
          children: [
            {
              path: 'cart',
              element: (
                <AuthGuard>
                  <UserShoppingCart />
                </AuthGuard>
              ),
            },
            {
              path: 'cafeteria-list',
              element: (
                <AuthGuard>
                  <UserCafeteriaList />
                </AuthGuard>
              ),
            },
            {
              path: 'payment/:orderId',
              element: (
                <AuthGuard>
                  <PaymentCheckoutPage />
                </AuthGuard>
              ),
            },
            {
              path: 'orders',
              element: (
                <AuthGuard>
                  <UserOrderHistory />
                </AuthGuard>
              ),
            },
            {
              path: 'order/track/:orderId',
              element: (
                <AuthGuard>
                  <UserOrderTracking />
                </AuthGuard>
              ),
            },
            {
              path: 'company-credits',
              element: (
                <AuthGuard>
                  <CompanyCafeCreditsPage />
                </AuthGuard>
              ),
            },
            {
              path: 'menu/:cafeteriaId',
              element: (
                <AuthGuard>
                  <UserMenuBrowsing />
                </AuthGuard>
              ),
            },
          ],
        },

        {
          path: 'virtual-office',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <MyVirtualOffice />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'virtual-office/renew/:subId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <VirtualOfficeRenewal />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'virtual-office/documents/:subId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <VirtualOfficeDocumentsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'downloads',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserDownloadPage />
            </RoleBasedGuard>
          ),
        },
      ],
    },

    {
      path: 'admin',
      element: (
        <AdminAuthGuard>
          <OrgLayout />
        </AdminAuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          element: (
            <RoleBasedGuard roles={['org']} permission="dashboard.view">
              <WedgeHomeRedirect>
                <CoWorkingDashboard />
              </WedgeHomeRedirect>
            </RoleBasedGuard>
          ),
        },
        {
          path: 'documentation',
          element: (
            // route-permission-exempt: the manual only describes pages, and it hides the sections you cannot reach
            <RoleBasedGuard roles={['org']}>
              <AdminDocumentation />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'developer',
          element: (
            <RoleBasedGuard roles={['org']} permission="developer.manage">
              <DeveloperSettingsPage />
            </RoleBasedGuard>
          ),
        },
        {
          // One API key on its own page: its permissions, its campuses and
          // the calls made with it.
          path: 'developer/keys/:keyId',
          element: (
            <RoleBasedGuard roles={['org']} permission="developer.manage">
              <ApiKeyDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Deep link into a guided tour. No permission on the route itself
          // because the permission depends on WHICH guide the URL names, which a
          // static guard cannot see. The engine checks it instead: coach.start
          // refuses any guide this user's codes cannot run, and the page says so
          // rather than spinning. An unknown id just lands on the dashboard.
          path: 'guides/:guideId',
          element: (
            // route-permission-exempt: the guide id decides the permission, so coach.start gates it
            <RoleBasedGuard roles={['org']}>
              <GuideLaunchPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'profile',
          element: (
            // route-permission-exempt: your own profile is yours
            <RoleBasedGuard roles={['org']}>
              <AdminProfileSettings />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'executive-dashboard',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="financials.view">
              <ExecutiveDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'dex',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager', 'staff'] }} slug="dex" permission="dashboard.view">
              <DexPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space/live-status',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="dashboard.view">
              <LiveStatus />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space/live-status/floor-plans',
          element: <Navigate to="/admin/space/live-control" replace />,
        },
        {
          path: 'space/live-control',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="dashboard.view">
              <LiveFloorPlanDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'check-ins',
          element: (
            <RoleBasedGuard roles={['org']} permission="checkins.view">
              <CheckinManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'community',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="community.view">
              <AdminFeedPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.view">
              <AdminEventsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/create',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.manage">
              <AdminEventFormPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/edit/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.manage">
              <AdminEventFormPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.view">
              <AdminEventDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.view">
              <SpaceManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/add',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.manage">
              <AddSpace />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/edit/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.manage">
              <EditSpace />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/floor-plans/list/',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.view">
              <FloorPlanList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/floor-plans-v2/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.manage">
              <FloorPlanBuilder />
            </RoleBasedGuard>
          ),
        },

        // The one plans page. The old plan screens below redirect here.
        {
          path: 'plans',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="plans.view">
              <PlansPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'plans/new',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="plans.manage">
              <PlanEditorPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'plans/:kind/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="plans.view">
              <PlanEditorPage />
            </RoleBasedGuard>
          ),
        },
        { path: 'plans-and-amenities', element: <RoomsAlias to="/admin/plans" /> },
        { path: 'plan-management', element: <RoomsAlias to="/admin/plans" /> },
        { path: 'plan-management/add', element: <RoomsAlias to="/admin/plans/new?type=day_pass" /> },
        { path: 'plan-management/edit/:id', element: <RoomsAlias to="/admin/plans/pass/:id" /> },
        {
          path: 'coupons',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.view">
              <CouponManagementPage />
            </RoleBasedGuard>
          ),
        },
        { path: 'monthly-plans', element: <RoomsAlias to="/admin/plans" /> },
        { path: 'create-monthly-plan', element: <RoomsAlias to="/admin/plans/new?type=monthly" /> },
        { path: 'edit-monthly-plan/:id', element: <RoomsAlias to="/admin/plans/plan/:id" /> },
        {
          path: 'communication',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.view">
              <CommunicationPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Website builder v1 is retired; the Website hub replaces it.
          path: 'website-builder',
          element: <Navigate to="/admin/leads-management/website" replace />,
        },
        {
          path: 'memberships',
          children: [
            {
              path: 'home',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <MembershipsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'summary',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <MemberSummary />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <MembershipManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <UserAdminProfile />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members/add',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.create">
                  <AddMember />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'subscriptions',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <SubscriptionsHome />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'subscriptions/auto-renewals',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <AutoRenewalManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'subscriptions/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <CompanySubscription />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'membership/:subscriptionId',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <SubscriptionOverview />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'visit-plans',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <VisitPlanManagement />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        // {
        //   path: 'financials',
        //   children: [
        //     {
        //       path: 'invoices',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <InvoiceManagement />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'receivables',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <ReceivablesManagement />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'pending-billables',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <PendingBillables />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'ledger',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <Ledger />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'credit-debit-notes',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <CreditDebitNotes />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //   ],
        // },
        {
          // Staff attendance for the operator's own team. "My attendance" is
          // deliberately ungated: seeing your own hours is not an admin power.
          path: 'workforce',
          children: [
            {
              // The landing page. Clicking Attendance used to open nothing and
              // wait for you to pick a screen off the menu beside it.
              index: true,
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <WorkforceDashboardPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'attendance',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <AttendanceRegisterPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'muster',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <MusterPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'staff',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <StaffDirectoryPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Leave.view guards the admin view of everybody's leave. Applying
              // for your own is self service inside the page, the same way "My
              // attendance" is, so the guard here is about seeing the team.
              path: 'leave',
              element: (
                <RoleBasedGuard roles={['org']} permission="leave.view">
                  <LeavePage />
                </RoleBasedGuard>
              ),
            },
            {
              // Raising a request about your own record is self service, the
              // same as My attendance. The guard here is about seeing, and
              // deciding, everybody else's.
              path: 'requests',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <AttendanceRequestsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'roster',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <RosterPlannerPage />
                </RoleBasedGuard>
              ),
            },
            {
              // The reports page carries ten reports on four different
              // permissions, so the route guard is the loosest of them and each
              // report gates itself server side. Guarding the route on any one
              // code would hide the payroll reports from a payroll manager who
              // cannot see attendance.
              path: 'reports',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  permissions={['attendance.view', 'leave.view', 'payroll.view', 'payroll.view_salary']}
                >
                  <WorkforceReportsPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Payroll settings sits under the payroll path but is a static
              // segment, so it is matched ahead of the run id and never gets
              // read as a run called "settings".
              path: 'payroll',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.view">
                  <PayrollRunsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'payroll/settings',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.process">
                  <PayrollSettingsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'payroll/:id',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.view">
                  <PayrollRunDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'advances',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.view">
                  <PayrollAdvancesPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'my-attendance',
              element: (
                // route-permission-exempt: seeing your own hours is not an admin power
                <RoleBasedGuard roles={['org']}>
                  <MyAttendancePage />
                </RoleBasedGuard>
              ),
            },
            {
              // Being somebody's manager is the permission. The backend builds
              // the list from who reports to you, so a code here would only
              // stop managers who do not hold it from seeing their own team.
              path: 'my-team',
              element: (
                // route-permission-exempt: a manager may always see their own reportees
                <RoleBasedGuard roles={['org']}>
                  <MyTeamPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Your own payslips, like your own attendance. No payroll
              // permission: everybody can see what they themselves were paid.
              path: 'my-pay',
              element: (
                // route-permission-exempt: everybody may see what they themselves were paid
                <RoleBasedGuard roles={['org']}>
                  <MyPayPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <WorkforceSettingsPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Guided setup. The landing page hands over to this on its own
              // while the module cannot yet record a day; this route is how
              // somebody comes back to it afterwards, from Settings.
              path: 'setup',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <WorkforceSetupWizard />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'order-management',
          // element: (
          //   <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
          //     <OrderList />
          //   </RoleBasedGuard>
          // ),
          children: [
            {
              path: 'list',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
                  <OrderList />
                </RoleBasedGuard>
              ),
            },
            {
              // Sales reaches this screen with subscriptions.manage alone, to
              // send the deal to accounts. The screen itself hides every
              // billing control without billing.create, and the backend
              // OrderView still demands billing.create to raise an invoice.
              // Reaching the screen is never permission to bill.
              path: 'create',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['billing.create', 'subscriptions.manage']}>
                  <CreateSubscription />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create-v2',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['billing.create', 'subscriptions.manage']}>
                  <CreateSubscriptionV2 />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'billing-handoffs',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['billing.view', 'subscriptions.manage']}>
                  <BillingHandoffList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create-visit',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.create">
                  <CreateVisitSubscription />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create-plan',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['subscriptions.manage', 'billing.create', 'plans.manage']}>
                  <CreatePlanSale />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'payment-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="financials.view">
                  <PaymentManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'bill-to-account',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="billing.view">
                  <BillToAccountManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'credit-notes',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
                  <CreditNoteList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'credit-notes/create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.manage">
                  <CreateCreditNote />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'collection-register',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="financials.view">
                  <CollectionRegisterPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'invoice-numbers',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="billing.numbering">
                  <InvoiceNumberManager />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'receipt/list/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="financials.view">
                  <ReceiptManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'receipt/create/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.manage">
                  <CreateReceiptPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'invoice/list/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
                  <InvoiceList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.view">
                  <ManualEntryListPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry/create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.create">
                  <ManualEntryCreatePage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry/view/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.view">
                  <ManualEntryViewPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry/edit/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.manage">
                  <ManualEntryEditPage />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'printing',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="printing.view">
              <PrintingManagement />
            </RoleBasedGuard>
          ),
        },

        {
          path: 'meeting-room-management',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.view">
              <MeetingRoomDashboard />
            </RoleBasedGuard>
          ),
        },
        // Unified plans Phase 5: credit packs are plans on the Credit packs tab.
        { path: 'meeting-room-credit-plans', element: <RoomsAlias to="/admin/plans?tab=credit-packs" /> },
        {
          path: 'meeting-room-management/calendar/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.view">
              <MeetingRoomCalendar />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'order-management/create-meeting-room',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.create">
              <CreateMeetingRoomOrder />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'create/meeting-room',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.settings">
              <CreateMeetingRoom />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'edit/meeting-room/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.settings">
              <EditMeetingRoom />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room-management/resource-types',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.settings">
              <ResourceTypes />
            </RoleBasedGuard>
          ),
        },
        // The Rooms spelling of the same screens. Aliases, not copies: each
        // one redirects to the meeting-room address, which stays canonical so
        // that nothing already bookmarked or handed out has to change.
        { path: 'rooms', element: <RoomsAlias to="/admin/meeting-room-management" /> },
        { path: 'rooms/calendar/:id', element: <RoomsAlias to="/admin/meeting-room-management/calendar/:id" /> },
        { path: 'rooms/resource-types', element: <RoomsAlias to="/admin/meeting-room-management/resource-types" /> },
        { path: 'room-credit-plans', element: <RoomsAlias to="/admin/plans?tab=credit-packs" /> },
        { path: 'create/room', element: <RoomsAlias to="/admin/create/meeting-room" /> },
        { path: 'edit/room/:id', element: <RoomsAlias to="/admin/edit/meeting-room/:id" /> },
        {
          path: 'org/member',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="team.view">
              <AddOrgMembers />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'compliance',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.view">
              <ComplianceDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'virtual-office',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <MyVirtualOffice />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'virtual-office/renew/:subId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <VirtualOfficeRenewal />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'virtual-office/documents/:subId',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <VirtualOfficeDocumentsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'downloads',
          element: (
            <RoleBasedGuard roles={['user']} allowed={{ user: ['admin', 'member'] }}>
              <UserDownloadPage />
            </RoleBasedGuard>
          ),
        },
      ],
    },

    {
      path: 'admin',
      element: (
        <AdminAuthGuard>
          <OrgLayout />
        </AdminAuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          element: (
            <RoleBasedGuard roles={['org']} permission="dashboard.view">
              <WedgeHomeRedirect>
                <CoWorkingDashboard />
              </WedgeHomeRedirect>
            </RoleBasedGuard>
          ),
        },
        {
          path: 'documentation',
          element: (
            // route-permission-exempt: the manual only describes pages, and it hides the sections you cannot reach
            <RoleBasedGuard roles={['org']}>
              <AdminDocumentation />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'developer',
          element: (
            <RoleBasedGuard roles={['org']} permission="developer.manage">
              <DeveloperSettingsPage />
            </RoleBasedGuard>
          ),
        },
        {
          // One API key on its own page: its permissions, its campuses and
          // the calls made with it.
          path: 'developer/keys/:keyId',
          element: (
            <RoleBasedGuard roles={['org']} permission="developer.manage">
              <ApiKeyDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Deep link into a guided tour. No permission on the route itself
          // because the permission depends on WHICH guide the URL names, which a
          // static guard cannot see. The engine checks it instead: coach.start
          // refuses any guide this user's codes cannot run, and the page says so
          // rather than spinning. An unknown id just lands on the dashboard.
          path: 'guides/:guideId',
          element: (
            // route-permission-exempt: the guide id decides the permission, so coach.start gates it
            <RoleBasedGuard roles={['org']}>
              <GuideLaunchPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'profile',
          element: (
            // route-permission-exempt: your own profile is yours
            <RoleBasedGuard roles={['org']}>
              <AdminProfileSettings />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'executive-dashboard',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="financials.view">
              <ExecutiveDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'dex',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager', 'staff'] }} slug="dex" permission="dashboard.view">
              <DexPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space/live-status',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="dashboard.view">
              <LiveStatus />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space/live-status/floor-plans',
          element: <Navigate to="/admin/space/live-control" replace />,
        },
        {
          path: 'space/live-control',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="dashboard.view">
              <LiveFloorPlanDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'check-ins',
          element: (
            <RoleBasedGuard roles={['org']} permission="checkins.view">
              <CheckinManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'community',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="community.view">
              <AdminFeedPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.view">
              <AdminEventsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/create',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.manage">
              <AdminEventFormPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/edit/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.manage">
              <AdminEventFormPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'events/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} slug="events" permission="events.view">
              <AdminEventDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.view">
              <SpaceManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/add',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.manage">
              <AddSpace />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/edit/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.manage">
              <EditSpace />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/floor-plans/list/',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.view">
              <FloorPlanList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'space-management/floor-plans-v2/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="spaces.manage">
              <FloorPlanBuilder />
            </RoleBasedGuard>
          ),
        },

        // The one plans page. The old plan screens below redirect here.
        {
          path: 'plans',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="plans.view">
              <PlansPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'plans/new',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="plans.manage">
              <PlanEditorPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'plans/:kind/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="plans.view">
              <PlanEditorPage />
            </RoleBasedGuard>
          ),
        },
        { path: 'plans-and-amenities', element: <RoomsAlias to="/admin/plans" /> },
        { path: 'plan-management', element: <RoomsAlias to="/admin/plans" /> },
        { path: 'plan-management/add', element: <RoomsAlias to="/admin/plans/new?type=day_pass" /> },
        { path: 'plan-management/edit/:id', element: <RoomsAlias to="/admin/plans/pass/:id" /> },
        {
          path: 'coupons',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.view">
              <CouponManagementPage />
            </RoleBasedGuard>
          ),
        },
        { path: 'monthly-plans', element: <RoomsAlias to="/admin/plans" /> },
        { path: 'create-monthly-plan', element: <RoomsAlias to="/admin/plans/new?type=monthly" /> },
        { path: 'edit-monthly-plan/:id', element: <RoomsAlias to="/admin/plans/plan/:id" /> },
        {
          path: 'communication',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.view">
              <CommunicationPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Website builder v1 is retired; the Website hub replaces it.
          path: 'website-builder',
          element: <Navigate to="/admin/leads-management/website" replace />,
        },
        {
          path: 'memberships',
          children: [
            {
              path: 'home',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <MembershipsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'summary',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <MemberSummary />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <MembershipManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.view">
                  <UserAdminProfile />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'members/add',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="members.create">
                  <AddMember />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'subscriptions',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <SubscriptionsHome />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'subscriptions/auto-renewals',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <AutoRenewalManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'subscriptions/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <CompanySubscription />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'membership/:subscriptionId',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <SubscriptionOverview />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'visit-plans',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="subscriptions.view">
                  <VisitPlanManagement />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        // {
        //   path: 'financials',
        //   children: [
        //     {
        //       path: 'invoices',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <InvoiceManagement />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'receivables',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <ReceivablesManagement />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'pending-billables',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <PendingBillables />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'ledger',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <Ledger />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //     {
        //       path: 'credit-debit-notes',
        //       element: (
        //         <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }}>
        //           <CreditDebitNotes />
        //         </RoleBasedGuard>
        //       ),
        //     },
        //   ],
        // },
        {
          // Staff attendance for the operator's own team. "My attendance" is
          // deliberately ungated: seeing your own hours is not an admin power.
          path: 'workforce',
          children: [
            {
              // The landing page. Clicking Attendance used to open nothing and
              // wait for you to pick a screen off the menu beside it.
              index: true,
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <WorkforceDashboardPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'attendance',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <AttendanceRegisterPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'muster',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <MusterPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'staff',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <StaffDirectoryPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Leave.view guards the admin view of everybody's leave. Applying
              // for your own is self service inside the page, the same way "My
              // attendance" is, so the guard here is about seeing the team.
              path: 'leave',
              element: (
                <RoleBasedGuard roles={['org']} permission="leave.view">
                  <LeavePage />
                </RoleBasedGuard>
              ),
            },
            {
              // Raising a request about your own record is self service, the
              // same as My attendance. The guard here is about seeing, and
              // deciding, everybody else's.
              path: 'requests',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <AttendanceRequestsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'roster',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <RosterPlannerPage />
                </RoleBasedGuard>
              ),
            },
            {
              // The reports page carries ten reports on four different
              // permissions, so the route guard is the loosest of them and each
              // report gates itself server side. Guarding the route on any one
              // code would hide the payroll reports from a payroll manager who
              // cannot see attendance.
              path: 'reports',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  permissions={['attendance.view', 'leave.view', 'payroll.view', 'payroll.view_salary']}
                >
                  <WorkforceReportsPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Payroll settings sits under the payroll path but is a static
              // segment, so it is matched ahead of the run id and never gets
              // read as a run called "settings".
              path: 'payroll',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.view">
                  <PayrollRunsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'payroll/settings',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.process">
                  <PayrollSettingsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'payroll/:id',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.view">
                  <PayrollRunDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'advances',
              element: (
                <RoleBasedGuard roles={['org']} permission="payroll.view">
                  <PayrollAdvancesPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'my-attendance',
              element: (
                // route-permission-exempt: seeing your own hours is not an admin power
                <RoleBasedGuard roles={['org']}>
                  <MyAttendancePage />
                </RoleBasedGuard>
              ),
            },
            {
              // Being somebody's manager is the permission. The backend builds
              // the list from who reports to you, so a code here would only
              // stop managers who do not hold it from seeing their own team.
              path: 'my-team',
              element: (
                // route-permission-exempt: a manager may always see their own reportees
                <RoleBasedGuard roles={['org']}>
                  <MyTeamPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Your own payslips, like your own attendance. No payroll
              // permission: everybody can see what they themselves were paid.
              path: 'my-pay',
              element: (
                // route-permission-exempt: everybody may see what they themselves were paid
                <RoleBasedGuard roles={['org']}>
                  <MyPayPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <WorkforceSettingsPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Guided setup. The landing page hands over to this on its own
              // while the module cannot yet record a day; this route is how
              // somebody comes back to it afterwards, from Settings.
              path: 'setup',
              element: (
                <RoleBasedGuard roles={['org']} permission="attendance.view">
                  <WorkforceSetupWizard />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'order-management',
          // element: (
          //   <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
          //     <OrderList />
          //   </RoleBasedGuard>
          // ),
          children: [
            {
              path: 'list',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
                  <OrderList />
                </RoleBasedGuard>
              ),
            },
            {
              // Sales reaches this screen with subscriptions.manage alone, to
              // send the deal to accounts. The screen itself hides every
              // billing control without billing.create, and the backend
              // OrderView still demands billing.create to raise an invoice.
              // Reaching the screen is never permission to bill.
              path: 'create',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['billing.create', 'subscriptions.manage']}>
                  <CreateSubscription />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create-v2',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['billing.create', 'subscriptions.manage']}>
                  <CreateSubscriptionV2 />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'billing-handoffs',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['billing.view', 'subscriptions.manage']}>
                  <BillingHandoffList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create-visit',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.create">
                  <CreateVisitSubscription />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create-plan',
              element: (
                <RoleBasedGuard roles={['org']} permissions={['subscriptions.manage', 'billing.create', 'plans.manage']}>
                  <CreatePlanSale />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'payment-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="financials.view">
                  <PaymentManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'bill-to-account',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="billing.view">
                  <BillToAccountManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'credit-notes',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
                  <CreditNoteList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'credit-notes/create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.manage">
                  <CreateCreditNote />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'collection-register',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="financials.view">
                  <CollectionRegisterPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'invoice-numbers',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="billing.numbering">
                  <InvoiceNumberManager />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'receipt/list/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="financials.view">
                  <ReceiptManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'receipt/create/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.manage">
                  <CreateReceiptPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'invoice/list/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.view">
                  <InvoiceList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.view">
                  <ManualEntryListPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry/create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.create">
                  <ManualEntryCreatePage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry/view/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.view">
                  <ManualEntryViewPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'manual-entry/edit/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="billing.manage">
                  <ManualEntryEditPage />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'printing',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="printing.view">
              <PrintingManagement />
            </RoleBasedGuard>
          ),
        },

        {
          path: 'meeting-room-management',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.view">
              <MeetingRoomDashboard />
            </RoleBasedGuard>
          ),
        },
        // Unified plans Phase 5: credit packs are plans on the Credit packs tab.
        { path: 'meeting-room-credit-plans', element: <RoomsAlias to="/admin/plans?tab=credit-packs" /> },
        {
          path: 'meeting-room-management/calendar/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.view">
              <MeetingRoomCalendar />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'order-management/create-meeting-room',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="billing.create">
              <CreateMeetingRoomOrder />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'create/meeting-room',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.settings">
              <CreateMeetingRoom />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'edit/meeting-room/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.settings">
              <EditMeetingRoom />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'meeting-room-management/resource-types',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="meeting_rooms.settings">
              <ResourceTypes />
            </RoleBasedGuard>
          ),
        },
        // The Rooms spelling of the same screens. Aliases, not copies: each
        // one redirects to the meeting-room address, which stays canonical so
        // that nothing already bookmarked or handed out has to change.
        { path: 'rooms', element: <RoomsAlias to="/admin/meeting-room-management" /> },
        { path: 'rooms/calendar/:id', element: <RoomsAlias to="/admin/meeting-room-management/calendar/:id" /> },
        { path: 'rooms/resource-types', element: <RoomsAlias to="/admin/meeting-room-management/resource-types" /> },
        { path: 'room-credit-plans', element: <RoomsAlias to="/admin/plans?tab=credit-packs" /> },
        { path: 'create/room', element: <RoomsAlias to="/admin/create/meeting-room" /> },
        { path: 'edit/room/:id', element: <RoomsAlias to="/admin/edit/meeting-room/:id" /> },
        {
          path: 'org/member',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="team.view">
              <AddOrgMembers />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'compliance',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.view">
              <ComplianceDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'lease-management',
          children: [
            {
              path: 'home',
              // An overview of leases this user can already open one by one, so
              // it reads on leases.view rather than on the write code.
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.view">
                  <LeaseManagementDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'leases',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.view">
                  <LeaseList />
                </RoleBasedGuard>
              ),
            },
            {
              // Portfolio view of every escalation clause, grouped by company.
              // Reads on leases.view; applying still writes through the
              // escalation-rule endpoints, which check leases.manage.
              path: 'escalations',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.view">
                  <LeaseEscalations />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'lease/:id/',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.view">
                  <LeaseDetail />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'edit/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <EditLease />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <CreateLease />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'tnc',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <LeaseTnC />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <LeaseSettings />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'signature-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <LeaseSingnature />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'addendum-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <AddendumManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'deposits',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="financials.view">
                  <DepositsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'template-builder',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leases.manage">
                  <LeaseTemplateBuilder />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'virtual-office',
          children: [
            {
              path: 'list',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="subscriptions.view">
                  <VirtualOfficeList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="subscriptions.manage">
                  <CreateVirtualOffice />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="subscriptions.view">
                  <VirtualOfficeDetail />
                </RoleBasedGuard>
              ),
            },
            {
              path: ':id/edit',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="subscriptions.manage">
                  <EditVirtualOffice />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'amenity-packages',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="plans.view">
                  <AmenityPackages />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'template-builder',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.manage">
                  <VOTemplateBuilder />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'leads-management',
          children: [
            // New sub-page routes
            {
              path: 'my-day',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <MyDayPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'team',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="team.view">
                  <LeadsTeamPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'team/:memberId',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <LeadsTeamMemberDayPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'reports',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="team.view">
                  <LeadsReportsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'dashboard',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <LeadsDashboardPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'leads/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <LeadDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'companies/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <LeadCompanyPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'leads',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <LeadsList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'follow-ups',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <FollowUpsPage />
                </RoleBasedGuard>
              ),
            },
            // Invitations not supported yet
            // {
            //   path: 'invitations',
            //   element: (
            //     <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }}>
            //       <LeadsInvitationsPage />
            //     </RoleBasedGuard>
            //   ),
            // },
            {
              path: 'deals',
              element: <Navigate to="../leads?view=deals" replace />,
            },
            {
              path: 'proposals',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <ProposalsList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'proposal/create',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.manage">
                  <ProposalCreationPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'proposal-settings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
                  <ProposalSettingsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'proposal-pdf-template',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
                  <ProposalPDFTemplatePage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'proposal-templates',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
                  <ProposalTemplateListPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'proposal-templates/:templateId',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
                  <ProposalTemplateBuilderPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Forms live in the Website hub now; old links land on its tab.
              path: 'forms',
              element: <Navigate to="/admin/leads-management/website?tab=forms" replace />,
            },
            {
              path: 'website',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <WebsiteHubPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Reading a form is leads.view so a rep can open Responses.
              // Everything that writes is gated inside the page on
              // leads.settings.
              path: 'forms/:id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
                  <LeadFormBuilderPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
                  <LeadSettingsPage />
                </RoleBasedGuard>
              ),
            },
            // Legacy redirects
            { path: 'home', element: <Navigate to="/admin/leads-management/dashboard" replace /> },
            { path: 'list', element: <Navigate to="/admin/leads-management/leads" replace /> },
            { path: 'home/settings', element: <Navigate to="/admin/leads-management/settings" replace /> },
            { path: 'communication', element: <Navigate to="/admin/communication" replace /> },
            { path: 'communication/', element: <Navigate to="/admin/communication" replace /> },
          ],
        },
        {
          path: 'outreach',
          element: <OutreachLayout />,
          children: [
            { index: true, element: <Navigate to="dashboard" replace /> },
            {
              path: 'dashboard',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'campaigns',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachCampaigns />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'campaigns/new',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <CampaignBuilderPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'campaigns/new/ai',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <AiCampaignWizardPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'campaigns/:campaignId/edit',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <CampaignBuilderPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'campaigns/:campaignId/detail',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachCampaignDetail />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'contacts',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachContacts />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'contacts/import/deskos',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachContactsImportDeskos />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'contacts/import/csv',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachContactsImportCsv />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'contacts/attributes',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachContactAttributes />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'templates',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachTemplates />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} slug="outreach" permission="settings.manage">
                  <OutreachSettings />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'service-requests',
          children: [
            {
              path: 'home',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'staff', 'center_manager', 'staff'] }}
                >
                  <ServiceRequestsDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'create',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'staff', 'center_manager', 'staff'] }}
                >
                  <TicketCreationForm />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'tickets',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'staff', 'center_manager', 'staff'] }}
                >
                  <TicketsList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'ticket/:id',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'staff', 'center_manager', 'staff'] }}
                >
                  <TicketDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'work-orders',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'staff', 'center_manager'] }}
                  permission="work_orders.view"
                >
                  <WorkOrdersList />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'work-orders/:id',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'staff', 'center_manager'] }}
                  permission="work_orders.view"
                >
                  <WorkOrderDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Gated on manage rather than view: a template is how the
              // operator runs their floor, not a task somebody is doing.
              path: 'work-order-templates',
              element: (
                <RoleBasedGuard
                  roles={['org']}
                  allowed={{ org: ['admin', 'manager', 'center_manager'] }}
                  permission="work_orders.manage"
                >
                  <WorkOrderTemplates />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings/category',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="service_requests.manage">
                  <ServiceCategory />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings/team',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="service_requests.manage">
                  <ServiceTeams />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="service_requests.manage">
                  <ServiceRequestsSettingsPage />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'visitor-management',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="visitors.view">
              <VisitorManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'courier',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager', 'staff'] }} permission="courier.view">
              <CourierManagement />
            </RoleBasedGuard>
          ),
        },
        {
          // Reading what the desk does is part of working the desk, so this is
          // gated on courier.view like the list. Changing anything needs
          // courier.settings, which the page itself checks.
          path: 'courier/settings',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager', 'staff'] }} permission="courier.view">
              <CourierSettings />
            </RoleBasedGuard>
          ),
        },
        {
          // The console. The screens themselves live at /screen/<token>,
          // outside every guard, because a TV on a wall never signs in.
          path: 'signage',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager', 'staff'] }} permission="signage.view" slug="signage">
              <SignageManagement />
            </RoleBasedGuard>
          ),
        },
        // ── Retired: the old Inventory Management module ──────────────
        // Everything it did now lives under /admin/expenses (Consumables for
        // stock, the ledger for money). These redirects exist only so old
        // bookmarks and links in already-sent emails still land somewhere
        // useful. Nothing links here from inside the app any more.
        {
          path: 'inventory-management',
          children: [
            { index: true, element: <Navigate to={PATH_ADMIN.expenses.consumables} replace /> },
            { path: 'home', element: <Navigate to={PATH_ADMIN.expenses.consumables} replace /> },
            { path: 'current-inventory', element: <Navigate to={PATH_ADMIN.expenses.consumablesInventory} replace /> },
            { path: 'item-page', element: <Navigate to={PATH_ADMIN.expenses.consumablesInventory} replace /> },
            { path: 'purchases', element: <Navigate to={PATH_ADMIN.expenses.consumablesPurchases} replace /> },
            { path: 'purchase-order', element: <Navigate to={PATH_ADMIN.expenses.consumablesPurchases} replace /> },
            { path: 'purchase/:id/edit', element: <Navigate to={PATH_ADMIN.expenses.consumablesPurchases} replace /> },
            { path: 'categories', element: <Navigate to={PATH_ADMIN.expenses.settingsCategories} replace /> },
            { path: 'vendors', element: <Navigate to={PATH_ADMIN.expenses.vendors} replace /> },
            { path: 'consumption', element: <Navigate to={PATH_ADMIN.expenses.consumablesConsumption} replace /> },
            { path: 'expense', element: <Navigate to={PATH_ADMIN.expenses.all} replace /> },
            { path: 'analytics', element: <Navigate to={PATH_ADMIN.expenses.reports} replace /> },
            { path: '*', element: <Navigate to={PATH_ADMIN.expenses.root} replace /> },
          ],
        },
        { path: 'expense', element: <Navigate to={PATH_ADMIN.expenses.all} replace /> },
        { path: 'expense/electricity', element: <Navigate to={PATH_ADMIN.expenses.all} replace /> },

        // ── Expenses section ──────────────────────────────────────────
        {
          path: 'expenses',
          children: [
            {
              index: true,
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="expenses.view">
                  <ExpensesHome />
                </RoleBasedGuard>
              ),
            },
            {
              // Control Center is money end to end, so it takes the financial
              // gate rather than the operational one. Same for Budgets and
              // Reports below, matching what the backend now enforces.
              path: 'dashboard',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="financials.view">
                  <ExpenseDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="expenses.view">
                  <ExpenseListPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Approvals stopped being an expenses thing once subscriptions
              // started raising them. The queue now lives in the Inbox.
              path: 'approvals',
              element: <Navigate to="/admin/inbox?tab=approvals" replace />,
            },
            {
              path: ':id',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="expenses.view">
                  <ExpenseDetailPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'vendors',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="expenses.view">
                  <ExpensesVendorsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'budgets',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="financials.view">
                  <BudgetsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'recurring',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="expenses.view">
                  <RecurringExpensesPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Cash in hand and cash out with people. Both are money, so it
              // takes the financial gate, matching CashReadPermission on the
              // endpoints behind it.
              path: 'petty-cash',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="financials.view">
                  <PettyCashPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'reports',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="financials.view">
                  <ExpenseReportsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="expenses.manage">
                  <ExpenseSettingsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings/categories',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="expenses.manage">
                  <ExpenseCategoriesPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings/approval-chains',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="expenses.manage">
                  <ApprovalChainsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'settings/access',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="expenses.manage">
                  <ExpenseAccessPage />
                </RoleBasedGuard>
              ),
            },
            {
              // Area per campus and per floor. Nothing else in the product
              // asks for it, and without it cost per sq ft cannot exist.
              path: 'settings/capacity',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="expenses.manage">
                  <ExpenseCapacityPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'consumables',
              children: [
                {
                  index: true,
                  element: (
                    <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="inventory.view">
                      <ConsumablesHub />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'inventory',
                  element: (
                    <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="inventory.view">
                      <ConsumablesInventory />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'purchases',
                  element: (
                    <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="inventory.view">
                      <ConsumablesPurchases />
                    </RoleBasedGuard>
                  ),
                },
                {
                  path: 'consumption',
                  element: (
                    <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="inventory.view">
                      <ConsumablesConsumption />
                    </RoleBasedGuard>
                  ),
                },
              ],
            },
          ],
        },

        {
          path: 'settlements',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="financials.view">
              <SettlementsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'deskos-billing',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'accountant'] }} permission="settings.view">
              <DeskOSBillingPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Booking a training call with us. Same people who see DeskOS billing,
          // because a session can be paid for or added to the next bill.
          path: 'deskos-training',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'accountant'] }}>
              <DeskOSTrainingPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'deskos-support',
          element: (
            <RoleBasedGuard roles={['org']} permission="deskos_support.view">
              <DeskOSSupportPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'accounting',
          children: [
            {
              path: 'billing-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'accountant'] }} permission="billing.manage">
                  <MonthlyBillingPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'invoice-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'accountant'] }} permission="billing.manage">
                  <InvoiceManagementPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'gst-management',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'accountant'] }} permission="billing.manage">
                  <GSTTaxManagement />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'notifications',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="dashboard.view">
              <AdminNotificationsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'reports',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="reports.view">
              <ReportsDashboard />
            </RoleBasedGuard>
          ),
        },

        // Analytics (new BI module)
        {
          path: 'analytics',
          element: (
            <RoleBasedGuard roles={['org']} permission="analytics.view">
              <AnalyticsHome />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'analytics/builder',
          element: (
            <RoleBasedGuard roles={['org']} permission="analytics.view">
              <AnalyticsReportBuilder />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'analytics/report/:code',
          element: (
            <RoleBasedGuard roles={['org']} permission="analytics.view">
              <AnalyticsReportViewer />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'analytics/dashboard/template/:code',
          element: (
            <RoleBasedGuard roles={['org']} permission="analytics.view">
              <AnalyticsDashboardPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'analytics/dashboard/:id',
          element: (
            <RoleBasedGuard roles={['org']} permission="analytics.view">
              <AnalyticsDashboardPage />
            </RoleBasedGuard>
          ),
        },

        {
          path: 'settings/general',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AdminSettings />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/roles',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="roles.manage">
              <RoleManagementPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/collection-accounts',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'accountant'] }} permission="billing.manage">
              <CollectionAccountsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/integrations',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <IntegrationsDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/integrations/knowlarity',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.view">
              <KnowlarityPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/integrations/nexudus',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.view">
              <NexudusPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/integrations/whatsapp',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <WhatsAppMsg91Page />
            </RoleBasedGuard>
          ),
        },
        {
          // Managing members against whatever portal the campus has connected.
          // Connecting one is a separate page under Settings -> Integrations,
          // because that is an integration and this is not.
          path: 'network',
          element: (
            <RoleBasedGuard roles={['org']} permission="wifi.view">
              <NetworkPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Vendor account, API key and site locations, for any portal.
          path: 'settings/integrations/captive-portal/:slug',
          element: (
            <RoleBasedGuard roles={['org']} permission="wifi.view">
              <CaptivePortalSetupPage />
            </RoleBasedGuard>
          ),
        },
        {
          // The old FirewallX-only setup screen. Kept as a redirect so saved
          // links and the automations setup CTA still land somewhere useful.
          path: 'settings/integrations/firewallx',
          element: <Navigate to="/admin/settings/integrations/captive-portal/firewallx" replace />,
        },
        {
          path: 'settings/whatsapp',
          element: (
            <RoleBasedGuard
              roles={['org']}
              allowed={{ org: ['admin'] }}
              slug="whatsapp_cloud"
              permission="settings.view"
            >
              <WhatsAppSettingsPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Credits are money, so this sits behind financials.view like every
          // other page in the product that shows a number with a currency on it.
          path: 'settings/ai-credits',
          element: (
            <RoleBasedGuard roles={['org']} permission="financials.view">
              <AICreditsPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Not financials.view: connecting your own AI client to data you can
          // already read grants nothing new, and the connector inherits your
          // permissions at every call. Gating this on money would stop a
          // non-financial admin from connecting at all rather than stopping
          // them from seeing amounts, which the skills already do.
          path: 'settings/ai-connections',
          element: (
            <RoleBasedGuard roles={['org']} permission="settings.view">
              <AIConnectionsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/integrations/gofloaters/bookings',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AggregatorBookingsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/integrations/tally',
          children: [
            {
              path: 'configure',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.manage">
                  <TallyConfigurationPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'export',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
                  <TallyExportPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'wizard',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.manage">
                  <TallyLedgerImportWizard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'mappings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.manage">
                  <CompanyMappingDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'connect',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.manage">
                  <TallyConnectPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'suggestions',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="settings.manage">
                  <AutoMatchSuggestions />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          // Xero / QuickBooks accounting sync. The callback is the OAuth redirect
          // target opened in a popup; it must sit inside the admin guard so the
          // session cookie/token is present when it POSTs the code to the backend.
          path: 'settings/integrations/accounting',
          children: [
            {
              index: true,
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
                  <AccountingIntegrationsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'callback',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
                  <AccountingOAuthCallback />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'settings/imports/',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <ImportCenter />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/imports/space',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <SpaceImportWizard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/imports/lease',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <LeaseImportWizard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/imports/lease/validate',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <LeaseValidationPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/imports/billing-address',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <BillingAddressImportPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/late-charges',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="billing.manage">
              <LateChargeManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'mealos/dashboard',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="cafe.view">
              <SpaceAdminDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'mealos/cafeteria',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="cafe.manage">
              <CafeteriaManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'mealos/staff-assignment',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="cafe.manage">
              <StaffAssignment />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'shop',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="cafe.manage">
              <ShopManagement />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'inspect',
          children: [
            {
              index: true,
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectOverviewPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'overview',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectOverviewPage />
                </RoleBasedGuard>
              ),
            },
            // Dashboard and Analytics were the two pages Overview replaced.
            // They stay routed at it so a bookmark or a link from elsewhere in
            // the console still lands somewhere useful.
            {
              path: 'dashboard',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectOverviewPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'records',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectSessionsPage />
                </RoleBasedGuard>
              ),
            },
            // The page moved to /records. This is kept so a link somebody
            // already pasted into an email still lands somewhere.
            {
              path: 'sessions',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectSessionsPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'issues',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectIssuesPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'analytics',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectOverviewPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'setup',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.manage">
                  <InspectManagement />
                </RoleBasedGuard>
              ),
            },
            // The page moved to /setup. This is kept so a link somebody
            // already pasted into an email still lands somewhere.
            {
              path: 'templates',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.manage">
                  <InspectManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'electricity',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="inspect.view">
                  <InspectElectricityPage />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'sync',
          children: [
            {
              path: 'home',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="reports.export">
                  <SyncDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'tally',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="reports.export">
                  <TallyDash />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'tally/mappings',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="reports.export">
                  <TallyMappingDashboard />
                </RoleBasedGuard>
              ),
            },
          ],
        },
        {
          path: 'downloads',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'staff'] }} permission="dashboard.view">
              <AdminDownloadPage />
            </RoleBasedGuard>
          ),
        },

        // Parking Management
        {
          path: 'parking',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="spaces.view">
              <ParkingManagementPage />
            </RoleBasedGuard>
          ),
        },

        // Asset Management
        {
          path: 'asset-management',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetManagementPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/settings',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.manage">
              <AssetSettingsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/assets/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetDetailPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/maintenance',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <MaintenancePage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/amc',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AMCPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/insurance',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetInsurancePage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/vendors',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetVendorsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/floor-map',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetFloorMapPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/analytics',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetAnalyticsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/damage',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetDamageReportsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/assignments',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetAssignmentsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/audits',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetAuditsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/audits/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetAuditRunPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'asset-management/scan',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="assets.view">
              <AssetScanPage />
            </RoleBasedGuard>
          ),
        },

        // Meeting Room Displays
        {
          path: 'settings/meeting-room-displays',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager', 'center_manager'] }} permission="settings.view">
              <MeetingRoomDisplaysPage />
            </RoleBasedGuard>
          ),
        },

        // Access Control
        {
          path: 'access-control',
          children: [
            {
              index: true,
              element: <Navigate to="/admin/access-control/users" replace />,
            },
            {
              path: 'users',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.view">
                  <AccessControlDashboard />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'cards',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.view">
                  <AccessProviderGuard capability="cards">
                    <CardAssignmentPage />
                  </AccessProviderGuard>
                </RoleBasedGuard>
              ),
            },
            {
              // Spintly SAAMS only. The older Spintly integration has no setup
              // screen of its own, so this guard is what keeps an ACAAS org off
              // a page whose fields would mean nothing to it.
              path: 'spintly-saams-setup',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="access_control.view">
                  <AccessProviderGuard provider="spintly_saams">
                    <SpintlySaamsSetupPage />
                  </AccessProviderGuard>
                </RoleBasedGuard>
              ),
            },
            {
              // Kisi-only. An org on another provider is sent back to the
              // access-control dashboard rather than shown a dead setup screen.
              path: 'kisi-setup',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="access_control.view">
                  <AccessProviderGuard provider="kisi">
                    <KisiSetupPage />
                  </AccessProviderGuard>
                </RoleBasedGuard>
              ),
            },
            {
              // Code Unlock only. The codes are a property of the doors, so the
              // page sits with the other access-control setup screens rather
              // than anywhere near the Meeting Room admin.
              path: 'code-unlock-setup',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.manage">
                  <AccessProviderGuard provider="code_unlock">
                    <CodeUnlockSetupPage />
                  </AccessProviderGuard>
                </RoleBasedGuard>
              ),
            },
            {
              path: 'zkbio-setup',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.manage">
                  <ZKBioSetupPage />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'event-logs',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.view">
                  {/* Both read the AccessEvent table. Hidden from the nav for a
                      provider that reports no events; guarded here too so a
                      bookmark does not land on a permanently empty page. */}
                  <AccessProviderGuard capability="event_push">
                    <AccessEventLogsPage />
                  </AccessProviderGuard>
                </RoleBasedGuard>
              ),
            },
            {
              path: 'insights',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.view">
                  {/* Both read the AccessEvent table. Hidden from the nav for a
                      provider that reports no events; guarded here too so a
                      bookmark does not land on a permanently empty page. */}
                  <AccessProviderGuard capability="event_push">
                    <AccessInsightsPage />
                  </AccessProviderGuard>
                </RoleBasedGuard>
              ),
            },
            {
              path: 'doors',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.manage">
                  <DoorManagement />
                </RoleBasedGuard>
              ),
            },
            {
              path: 'doors/:spaceId/mapping',
              element: (
                <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="access_control.manage">
                  <DoorManagementMapping />
                </RoleBasedGuard>
              ),
            },
          ],
        },

        // Automations — unified list
        {
          path: 'automations',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AutomationsPage />
            </RoleBasedGuard>
          ),
        },
        {
          // Approvals and exceptions, one page. Not permission gated as a
          // whole: the approvals tab is the requests routed to this person, so
          // anyone who can be an approver can open it. The exceptions tab gates
          // itself inside the page.
          path: 'inbox',
          element: (
            // route-permission-exempt: the inbox is what was routed to you, and each tab gates itself
            <RoleBasedGuard roles={['org']}>
              <InboxPage />
            </RoleBasedGuard>
          ),
        },
        // Old bookmarks. Exceptions moved into the Inbox.
        {
          path: 'exceptions',
          element: <Navigate to="/admin/inbox?tab=exceptions" replace />,
        },
        // Visual rule builder — new rule
        {
          path: 'automations/builder',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AutomationBuilderPage />
            </RoleBasedGuard>
          ),
        },
        // Visual rule builder — edit an existing event automation.
        // Declared before the invoice edit route because both are two segments
        // deep and this one's static "event" segment must win.
        {
          path: 'automations/builder/event/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AutomationBuilderPage />
            </RoleBasedGuard>
          ),
        },
        // Visual rule builder — edit existing rule
        {
          path: 'automations/builder/:id',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AutomationBuilderPage />
            </RoleBasedGuard>
          ),
        },
        // Legacy routes kept so existing links don't 404
        {
          path: 'automations/runs',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AutomationRunsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'automations/rules',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin'] }} permission="settings.manage">
              <AutomationsPage />
            </RoleBasedGuard>
          ),
        },

        // Chatbots
        {
          path: 'chatbots',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
              <ChatbotDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'chatbots/dashboard',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
              <ChatbotDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'chatbots/settings',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
              <ChatbotsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'chatbots/:botId/builder',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
              <ChatbotBuilder />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'chatbots/inbox',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.view">
              <ChatbotInbox />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'chatbots/knowledge',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
              <ChatbotKnowledge />
            </RoleBasedGuard>
          ),
        },
        {
          // Last of the chatbot routes deliberately. A bot id is a uuid and
          // could never collide with 'settings' or 'inbox', but the reader
          // should not have to know that to be sure.
          path: 'chatbots/:botId',
          element: (
            <RoleBasedGuard roles={['org']} allowed={{ org: ['admin', 'manager'] }} permission="leads.settings">
              <ChatbotAiPage />
            </RoleBasedGuard>
          ),
        },
      ],
    },
    {
      path: 'restaurant-admin',
      element: (
        <AdminAuthGuard>
          <OrgLayout />
        </AdminAuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          element: <RestaurantAdminDashboard />,
        },
        {
          path: 'menu-management',
          element: <MenuManagement />,
        },
        {
          path: 'orders-management',
          element: <OrdersManagement />,
        },
        {
          path: 'kitchen-display',
          element: <KitchenDisplaySystem />,
        },
        {
          path: 'inventory-management',
          element: <RestaurantInventoryManagement />,
        },
        {
          path: 'raw-inventory',
          element: <InventoryLayout />,
          children: [
            { index: true, element: <InventoryDashboardPage /> },
            { path: 'stock', element: <InventoryStockPage /> },
            { path: 'purchasing', element: <InventoryPurchasingPage /> },
            { path: 'grns', element: <InventoryGrnsPage /> },
            { path: 'stock-takes', element: <InventoryStockTakesPage /> },
            { path: 'prepared', element: <InventoryPreparedPage /> },
            { path: 'vendors', element: <InventoryVendorsPage /> },
            { path: 'categories', element: <InventoryCategoriesPage /> },
            { path: 'reports', element: <InventoryReportsPage /> },
          ],
        },
        {
          path: 'integration',
          element: <RazorpayIntegrationPage />,
        },
        {
          path: 'staff-assignment',
          element: <RestaurantStaffAssignment />,
        },
        {
          path: 'settings',
          element: <CafeSettings />,
        },
        {
          path: 'pos',
          element: <CafePOSPage />,
        },
        {
          path: 'coupons',
          element: <CafeCouponsPage />,
        },
        {
          path: 'meal-schedule',
          element: <MealSchedulePage />,
        },
        {
          path: 'import',
          element: <RestaurantImportCenterPage />,
        },
        {
          path: 'menu-import',
          element: <MenuImportPage />,
        },
        {
          path: 'raw-material-import',
          element: <RawMaterialImportPage />,
        },
      ],
    },
    {
      path: 'chef',
      element: (
        <AdminAuthGuard>
          <OrgLayout />
        </AdminAuthGuard>
      ),
      children: [
        {
          path: 'kitchen-display',
          element: <KitchenDisplaySystem />,
        },
        {
          path: 'pos',
          element: <CafePOSPage />,
        },
        {
          path: 'orders-management',
          element: <OrdersManagement />,
        },
        {
          path: 'printer-settings',
          element: <ChefPrinterSettingsPage />,
        },
      ],
    },
    {
      path: 'auth',
      children: [
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'create-password', element: <NewUserResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
        { path: 'crossing', element: <Crossing /> },
      ],
    },
    {
      path: 'cik',
      element: (
        <KioskAuthGuard>
          <KioskLayout />
        </KioskAuthGuard>
      ),
      children: [
        {
          path: 'home',
          element: <CIKHome />,
        },
        {
          path: 'scanner',
          element: <CheckInScanner />,
        },
        {
          path: 'checkout',
          element: <CheckoutScanner />,
        },
      ],
    },
    {
      path: 'payments',
      children: [
        {
          path: 'pending/:id',
          element: <PaymentReqDetail />,
        },
        {
          path: 'success',
          element: <PaymentReqSuccess />,
        },
      ],
    },

    {
      path: 'boss',
      element: (
        <AdminAuthGuard>
          <OrgLayout />
        </AdminAuthGuard>
      ),
      children: [
        {
          path: 'order/list',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossOrderList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'order/detail/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossOrderDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'order/create',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossCreateOrder />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'order/create-clubbed',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossCreateClubbedOrder />
            </RoleBasedGuard>
          ),
        },
        {
          // Retired full-page order editor — it called a pre-namespace API
          // (/admin/orders/…) that no longer exists, so it could only ever
          // render "Failed to load order details". Editing now happens in
          // DeskOSOrderEditDialog off the order list; old links land there.
          path: 'order/edit/:id',
          element: <Navigate to="/boss/order/list" replace />,
        },
        {
          // Retired super_admin org pages — repointed to the canonical manager pages
          // so any stray links land on the single shared (ownership-scoped) org view.
          path: 'organization/list',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossManagerOrgList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'organization/detail/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossManagerOrgDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'billing',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossMonthlyBilling />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'settings/email',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossEmailSettings />
            </RoleBasedGuard>
          ),
        },
        {
          // Reads across every tenant on purpose, which is why it lives here and not
          // behind any org-scoped route.
          path: 'whatsapp',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossWhatsAppConsole />
            </RoleBasedGuard>
          ),
        },
        {
          // Hands an org the second factor on a root shell inside their own office.
          // super_admin only, and deliberately not something a relationship manager
          // can do on a customer's behalf.
          path: 'printly',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossPrintlyAccess />
            </RoleBasedGuard>
          ),
        },
        {
          // Decides what code runs on every Pi in the field. super_admin only for
          // the same reason as the page above: publishing a release is the widest
          // blast radius any single action in this product has.
          path: 'printly/releases',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossPrintlyReleases />
            </RoleBasedGuard>
          ),
        },
        {
          // Vendor cost, markup and margin are on this page and nowhere else in
          // the product, so it is super_admin only rather than any boss role.
          path: 'ai-credits',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossAICredits />
            </RoleBasedGuard>
          ),
        },
        {
          // Which plan modules each org actually uses. Managers and RMs see
          // their own orgs (scoped backend-side); thresholds are super_admin only.
          path: 'adoption',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossAdoption />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'training/requests',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: TRAINING_ROLES }}>
              <BossTrainingRequests />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'training/sessions',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: TRAINING_ROLES }}>
              <BossTrainingSessions />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'training/settings',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: TRAINING_ROLES }}>
              <BossTrainingSettings />
            </RoleBasedGuard>
          ),
        },
        {
          // The catalog every org buys credits and add-ons from, and the money
          // that moves when they do, so it is super_admin only.
          path: 'store',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossStore />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'support/config',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <BossSupportConfig />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'support/tickets',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'relationship_manager'] }}>
              <BossSupportTickets />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'support/tickets/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'relationship_manager'] }}>
              <BossSupportTicketDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'organization/create',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossOrgOnboarding />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'manager/organizations',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossManagerOrgList />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'manager/organizations/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BOSS_STAFF_ROLES }}>
              <BossManagerOrgDetail />
            </RoleBasedGuard>
          ),
        },

        // ── Sales CRM ──
        {
          path: 'crm/my-day',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'manager'] }}>
              <CrmMyDayPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/dashboard',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <CrmSalesDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/accounts',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'manager'] }}>
              <CrmAccountsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/accounts/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'manager'] }}>
              <CrmAccountDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/activities',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'manager'] }}>
              <CrmActivitiesPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/contacts',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'manager'] }}>
              <CrmContactsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/team',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <CrmTeamDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'crm/team/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin'] }}>
              <CrmTeamDashboard />
            </RoleBasedGuard>
          ),
        },
        {
          // Same gate as the pipeline itself. A connection can do nothing its
          // owner could not already do by opening these pages by hand.
          path: 'crm/connections',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'manager'] }}>
              <CrmConnectionsPage />
            </RoleBasedGuard>
          ),
        },

        // ── deskos.net content ──
        // The only section a publisher can reach. Mirrors MARKETING_ROLES in
        // marketing/boss/permissions.py, which is where it is enforced.
        {
          path: 'website/posts',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'publisher'] }}>
              <WebsitePostsPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'website/posts/new',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'publisher'] }}>
              <WebsitePostEditor />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'website/posts/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'publisher'] }}>
              <WebsitePostEditor />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'website/taxonomy',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'publisher'] }}>
              <WebsiteTaxonomyPage />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'website/media',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: ['super_admin', 'publisher'] }}>
              <WebsiteMediaPage />
            </RoleBasedGuard>
          ),
        },

        // ── Build: sprints, stories and deploys ──
        // Every screen here is open to all three build roles, the same as the
        // API. The founder-only actions (merge, repository writes) are hidden
        // by `me.can_merge` on the page rather than by a second gate here, so
        // the console and deskos_billing/sprints/permissions.py cannot drift.
        {
          path: 'build',
          element: <Navigate to="/boss/build/my-work" replace />,
        },
        {
          path: 'build/my-work',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildMyWork />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'build/board',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildBoard />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'build/backlog',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildBacklog />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'build/sprints',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildSprints />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'build/deploys',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildDeploys />
            </RoleBasedGuard>
          ),
        },
        {
          // The link every nudge email and Telegram message points at. Keep
          // this path exactly as it is: deskos_billing/sprints/notify.py
          // builds it by hand.
          path: 'build/deploy/:id',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildDeployDetail />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'build/queue',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildQueue />
            </RoleBasedGuard>
          ),
        },
        {
          path: 'build/repos',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <BuildRepos />
            </RoleBasedGuard>
          ),
        },
        {
          // The same tokens as crm/connections. The server decides what each
          // one reaches from its owner's role, so this is only the doorway.
          path: 'build/connections',
          element: (
            <RoleBasedGuard roles={['boss']} allowed={{ boss: BUILD_ROLES }}>
              <CrmConnectionsPage variant="build" />
            </RoleBasedGuard>
          ),
        },
      ],
    },

    // Guest Routes
    {
      path: 'admin/login',
      element: (
        <GuestGuard type="admin">
          <AdminLogin />
        </GuestGuard>
      ),
    },

    {
      path: 'cik/login',
      element: (
        <GuestGuard type="kiosk">
          <KioskLogin />
        </GuestGuard>
      ),
    },

    {
      path: 'user/login',
      element: (
        <GuestGuard type="user">
          <UserLogin />
        </GuestGuard>
      ),
    },
    {
      path: 'plans',
      element: (
        <GuestGuard type="admin">
          <PublicPlans />
        </GuestGuard>
      ),
    },
    {
      path: '/meeting-room/:campusId/:roomId',
      element: (
        <GuestGuard type="admin">
          <PublicMeetingRoom />
        </GuestGuard>
      ),
    },
    {
      // Free-tier public booking link (path-based, app.deskos.net/book/...)
      path: '/book/:orgSlug/:campusId/:roomId',
      element: (
        <GuestGuard type="admin">
          <PublicMeetingRoom />
        </GuestGuard>
      ),
    },
    {
      path: '/meeting-rooms/:orgSlug',
      element: <PublicMeetingRoomDirectory />,
    },
    // The Rooms spelling of the public links. The meeting-room addresses
    // above stay canonical, because those are the ones already printed on
    // doors and sent to guests, and these land on the same pages.
    {
      path: '/room/:campusId/:roomId',
      element: <RoomsAlias to="/meeting-room/:campusId/:roomId" />,
    },
    {
      path: '/rooms/:orgSlug',
      element: <RoomsAlias to="/meeting-rooms/:orgSlug" />,
    },
    {
      path: '/room-credits/:campusId',
      element: <RoomsAlias to="/meeting-room-credits/:campusId" />,
    },
    {
      // Public and unguarded: the terms and the privacy notice have to be
      // readable before anyone has an account, which is the whole point of
      // linking them from the login screen.
      path: '/legal/:docType',
      element: <LegalDocumentPage />,
    },
    {
      // A company invite link. Open to signed-in members too, because a guest
      // who booked first joins their company from this same link.
      path: '/join/:code',
      element: <JoinCompany />,
    },
    {
      path: '/day-pass/:campusId/:planId',
      element: (
        <GuestGuard type="admin">
          <PublicDayPass />
        </GuestGuard>
      ),
    },
    {
      path: 'get-started',
      children: [
        { index: true, element: <SignupPage /> },
        { path: 'onboarding', element: <SignupOnboardingWizard /> },
      ],
    },
    {
      path: '/chat/:botId',
      element: <ChatWidgetPage />,
    },
    {
      // Where the QR label on a chair leads. Open on purpose: the page itself
      // decides whether the scanner is staff and redirects them if so.
      path: '/a/:code',
      element: <PublicAssetPage />,
    },
    {
      // Authorised by the signed state token in the query string, not by a session:
      // this runs on connect.deskos.net, where the tenant has no login.
      path: '/whatsapp/connect',
      element: <WhatsAppConnect />,
    },
    {
      path: '/cafe/:cafeId',
      element: <PublicCafeteriaLanding />,
    },
    {
      path: '/cafes/:campusId',
      element: <PublicCampusCafeterias />,
    },
    {
      path: '/c/:code',
      element: <ShortLinkRedirect />,
    },
    {
      path: '/meeting-room-credits/:campusId',
      element: (
        <GuestGuard type="admin">
          <PublicMeetingRoomCredits />
        </GuestGuard>
      ),
    },
    {
      path: 'landing',
      element: (
        <GuestGuard type="admin">
          <LandingPage />
        </GuestGuard>
      ),
    },
    {
      path: 'invoice/payment/:token',
      element: <PaymentPage />,
    },
    {
      path: 'lease/signing/:signingToken',
      element: <LeaseSigningPage />,
    },
    {
      path: 'addendum/signing/:signingToken',
      element: <AddendumSigningPage />,
    },
    {
      path: 'addendum/owner-signing/:ownerToken',
      element: <AddendumOwnerSigningPage />,
    },
    {
      path: 'lease-admin/signing/:signingToken',
      element: <LeaseAdminSigningPage />,
    },
    {
      path: 'vo/signing/:signingToken',
      element: <VOSigningPage />,
    },
    {
      path: 'vo-admin/signing/:signingToken',
      element: <VOAdminSigningPage />,
    },
    {
      path: 'public/visitor-entry',
      element: <VisitorEntryHome />,
    },
    {
      path: 'public/visitor-entry/:campusId',
      element: <VisitorEntryHome />,
    },
    {
      path: 'proposal/:proposalId',
      element: <PublicProposalPage />,
    },
    // A lead form, open to anyone. The same page serves the standalone link
    // and the iframe the embed script drops into an operator's own website,
    // which is why there is one route and not two: ?embed=1 only changes the
    // chrome around the form.
    {
      path: 'f/:ident',
      element: <PublicLeadFormPage />,
    },
    // Book a tour, and the link in every tour email to move or cancel one.
    // Open to anyone, for the same reason as a lead form. The manage route is
    // listed first so its fixed segment is never read as a tour id.
    {
      path: 't/manage/:token',
      element: <TourManagePage />,
    },
    {
      path: 't/:ident',
      element: <PublicTourPage />,
    },
    // Digital signage. A screen on a wall has no user and never signs in, so
    // both of these sit outside every guard: the token in the URL is the whole
    // credential, and the PIN, when the screen has one, is asked for by the
    // page itself.
    {
      path: 'screen',
      element: <SignagePairingPage />,
    },
    {
      path: 'screen/:token',
      element: <SignagePlayerPage />,
    },

    {
      path: '/',
      element: <Navigate to="/auth/crossing" replace />,
    },

    // Live inventory for brokers an operator invites, and the operator's public
    // no-login link (p/<token>). Read only, outside every
    // admin and member layout.
    {
      path: 'live-inventory',
      children: [
        { path: 'login', element: <LiveInventoryLogin /> },
        { path: 'set-password', element: <LiveInventorySetPassword /> },
        { path: 'p/:token', element: <LiveInventoryPublicPage /> },
        {
          path: '',
          element: (
            <BrokerGuard>
              <LiveInventoryPage />
            </BrokerGuard>
          ),
        },
      ],
    },

    // Other
    // ── Atlas — Sales Tablet ───────────────────────────────────────────────────
    {
      path: 'atlas',
      children: [
        {
          path: 'login',
          element: <AtlasLoginPage />,
        },
        {
          path: '',
          element: (
            <AdminAuthGuard>
              <AtlasLayout />
            </AdminAuthGuard>
          ),
          children: [
            { index: true, element: <Navigate to="/atlas/dashboard" replace /> },
            { path: 'dashboard', element: <AtlasDashboardPage /> },
            { path: 'inventory', element: <InventoryBoardPage /> },
            { path: 'inventory/:id', element: <SpaceDetailPage /> },
            { path: 'leads', element: <AtlasLeadsPage /> },
            { path: 'leads/create', element: <AtlasLeadFormPage /> },
            { path: 'leads/:id', element: <AtlasLeadDetailPage /> },
            { path: 'leads/:id/edit', element: <AtlasLeadFormPage /> },
            { path: 'proposals', element: <AtlasProposalsPage /> },
            { path: 'proposals/create', element: <AtlasProposalCreatePage /> },
            { path: 'proposals/:id', element: <AtlasProposalDetailPage /> },
            { path: 'proposals/:id/edit', element: <AtlasProposalCreatePage /> },
            { path: 'calendar', element: <AtlasCalendarPage /> },
          ],
        },
      ],
    },

    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
        { path: 'unknown', element: <WrongSubdomainPage /> },
      ],
    },

    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));

const StudentLogin = Loadable(lazy(() => import('../pages/auth/StudentLogin')));
const UniversityLogin = Loadable(lazy(() => import('../pages/auth/UniversityLogin')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const Signup = Loadable(lazy(() => import('../pages/auth/Signup')));
const VerifyOTP = Loadable(lazy(() => import('../pages/auth/OTPVerification')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewUserResetPassword = Loadable(lazy(() => import('../pages/auth/NewUserResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// User
const UserDashboard = Loadable(lazy(() => import('../pages/user/userDashboard/UserDashboard')));
const UserSettings = Loadable(lazy(() => import('../pages/user/Settings')));
const MemberDoorsPage = Loadable(lazy(() => import('../pages/user/doorAccess/MemberDoorsPage')));
const UserFeed = Loadable(lazy(() => import('../pages/user/feed/UserFeed')));
const PostDetailPage = Loadable(lazy(() => import('../pages/user/feed/PostDetailPage')));
const UserEventsPage = Loadable(lazy(() => import('../pages/user/events/UserEventsPage')));
const UserEventDetailPage = Loadable(lazy(() => import('../pages/user/events/UserEventDetailPage')));
const SpaceListingPage = Loadable(lazy(() => import('../pages/user/space/SpaceListingPage')));
const PurchaseSpace = Loadable(lazy(() => import('../pages/user/space/PurchaseSpace')));
const BundleSelectionPage = Loadable(lazy(() => import('../pages/user/space/BundleSelection')));
const CheckoutPage = Loadable(lazy(() => import('../pages/user/orders/CheckoutPage')));
const CheckoutSuccess = Loadable(lazy(() => import('../pages/user/orders/CheckoutSuccess')));
const UserProfile = Loadable(lazy(() => import('../pages/user/profile/UserProfile')));

const SeatSelectorPage = Loadable(lazy(() => import('../pages/user/space/SeatSelectorPage')));
const MeetingRoomUserList = Loadable(lazy(() => import('../pages/user/meetingRoom/MeetingRoomList')));
const MeetingRoomUserBooking = Loadable(lazy(() => import('../pages/user/meetingRoom/MeetingRoomBooking')));
const MeetingRoomUserDetail = Loadable(lazy(() => import('../pages/user/meetingRoom/MeetingRoomDetail')));
const MeetingRoomUserExtend = Loadable(lazy(() => import('../pages/user/meetingRoom/MeetingRoomExtend')));
const MeetingRoomUserCheckout = Loadable(lazy(() => import('../pages/user/meetingRoom/MeetingRoomCheckout')));
const MeetingRoomBookingSuccess = Loadable(lazy(() => import('../pages/user/meetingRoom/MeetingRoomBookingSuccess')));
const MyMeetingRoomBooking = Loadable(lazy(() => import('../pages/user/meetingRoom/MyBookings')));

const MyVisits = Loadable(lazy(() => import('../pages/user/visits/MyVisits')));
const MyOrders = Loadable(lazy(() => import('../pages/user/orders/MyOrders')));

const TeamManagementDashboard = Loadable(lazy(() => import('../pages/user/teamManagement/TeamManagementDashboard')));
const AttendanceDashboard = Loadable(lazy(() => import('../pages/user/teamManagement/AttendanceDashboard')));
const AttendanceSettings = Loadable(lazy(() => import('../pages/user/teamManagement/AttendanceSettings')));
const MemberAttendance = Loadable(lazy(() => import('../pages/user/teamManagement/MemberAttendance')));
const AttendanceReport = Loadable(lazy(() => import('../pages/user/teamManagement/AttendanceReportPage')));
const TeamMembers = Loadable(lazy(() => import('../pages/user/teamManagement/TeamMembers')));
const CompanySettings = Loadable(lazy(() => import('../pages/user/compManagement/CompanySettings')));
const CompanyBilling = Loadable(lazy(() => import('../pages/user/teamManagement/BillingDashboard')));
const UserServiceRequests = Loadable(lazy(() => import('../pages/user/serviceRequests/ServiceRequests')));
const UserServiceRequestForm = Loadable(lazy(() => import('../pages/user/serviceRequests/ServiceRequestForm')));
const UserServiceRequestDetail = Loadable(lazy(() => import('../pages/user/serviceRequests/ServiceRequestDetailPage')));
const MyCourier = Loadable(lazy(() => import('../pages/user/courier/MyCourier')));
const MyRefreshments = Loadable(lazy(() => import('../pages/user/refreshments/MyRefreshments')));
const UserPrintingHome = Loadable(lazy(() => import('../pages/user/printing/PrintingService')));
const UserPrintingNew = Loadable(lazy(() => import('../pages/user/printing/NewPrint')));
const UserPrintingCheckout = Loadable(lazy(() => import('../pages/user/printing/PrintJobCheckout')));
const UserPrintingDetail = Loadable(lazy(() => import('../pages/user/printing/PrintJobDetail')));
const UserPublicHome = Loadable(lazy(() => import('../pages/user/public/Home')));
const UserDownloadPage = Loadable(lazy(() => import('../pages/user/downloads/Downloads')));
const UserVisitorManagement = Loadable(lazy(() => import('../pages/user/visitorManagement/UserVisitorManagement')));
const UserCafeteriaList = Loadable(lazy(() => import('../pages/user/mealos/UserCafeteriaList')));
const UserMenuBrowsing = Loadable(lazy(() => import('../pages/user/mealos/UserMenuBrowsing')));
const UserOrderHistory = Loadable(lazy(() => import('../pages/user/mealos/UserOrderHistory')));
const UserOrderTracking = Loadable(lazy(() => import('../pages/user/mealos/UserOrderTracking')));
const UserShoppingCart = Loadable(lazy(() => import('../pages/user/mealos/UserShoppingCart')));
const PaymentCheckoutPage = Loadable(lazy(() => import('../pages/user/mealos/PaymentCheckoutPage')));
const CompanyCafeCreditsPage = Loadable(lazy(() => import('../pages/user/mealos/CompanyCafeCreditsPage')));
const MyVirtualOffice = Loadable(lazy(() => import('../pages/user/virtualOffice/MyVirtualOffice')));
const VirtualOfficeRenewal = Loadable(lazy(() => import('../pages/user/virtualOffice/VirtualOfficeRenewal')));
const VirtualOfficeDocumentsPage = Loadable(lazy(() => import('../pages/user/virtualOffice/VirtualOfficeDocumentsPage')));

// Admin
const SpaceManagement = Loadable(lazy(() => import('../pages/admin/spaceManagement/spaceManagement')));
const AddSpace = Loadable(lazy(() => import('../pages/admin/spaceManagement/AddSpace')));
const EditSpace = Loadable(lazy(() => import('../pages/admin/spaceManagement/EditSpace')));
const FloorPlanBuilder = Loadable(lazy(() => import('../pages/admin/spaceManagement/floorPlanV3/FloorPlanBuilder')));
const LiveFloorPlanDashboard = Loadable(lazy(() => import('../pages/admin/spaceManagement/LiveFloorPlanDashboard')));

const FloorPlanList = Loadable(lazy(() => import('../pages/admin/spaceManagement/floorPlan/FloorPlanList')));
const DoorManagement = Loadable(lazy(() => import('../pages/admin/spaceManagement/door/DoorManagement')));
const DoorManagementMapping = Loadable(
  lazy(() => import('../pages/admin/spaceManagement/door/SpaceDoorMapping'))
);

const LiveStatus = Loadable(lazy(() => import('../pages/admin/spaceManagement/LiveStatus')));
const AdminFeedPage = Loadable(lazy(() => import('../pages/admin/community/AdminFeedPage')));
const AdminEventsPage = Loadable(lazy(() => import('../pages/admin/events/AdminEventsPage')));
const AdminEventFormPage = Loadable(lazy(() => import('../pages/admin/events/AdminEventFormPage')));
const AdminEventDetailPage = Loadable(lazy(() => import('../pages/admin/events/AdminEventDetailPage')));
const PlansPage = Loadable(lazy(() => import('../pages/admin/planManagement/plans/PlansPage')));
const PlanEditorPage = Loadable(lazy(() => import('../pages/admin/planManagement/plans/PlanEditorPage')));
const CouponManagementPage = Loadable(lazy(() => import('../pages/admin/planManagement/Coupons/CouponManagementPage')));

const CheckinManagement = Loadable(lazy(() => import('../pages/admin/checkIns/CheckIns')));
const MembershipManagement = Loadable(lazy(() => import('../pages/admin/membershipManagement/MembershipManagement')));
const MembershipsPage = Loadable(lazy(() => import('../pages/admin/membershipManagement/MembershipsPage')));
const UserAdminProfile = Loadable(lazy(() => import('../pages/admin/membershipManagement/UserProfile')));
const MemberSummary = Loadable(lazy(() => import('../pages/admin/membershipManagement/MemberSummary')));
const SubscriptionsHome = Loadable(
  lazy(() => import('../pages/admin/membershipManagement/subscriptions/SubscriptionsHome'))
);
const AutoRenewalManagement = Loadable(
  lazy(() => import('../pages/admin/membershipManagement/renewals/RenewalManagement'))
);
const InboxPage = Loadable(lazy(() => import('../pages/admin/inbox/InboxPage')));
const VisitPlanManagement = Loadable(lazy(() => import('../pages/admin/membershipManagement/VisitPlans')));
const CompanySubscription = Loadable(
  lazy(() => import('../pages/admin/membershipManagement/subscriptions/SubscriptionDetailsPage'))
);
const SubscriptionOverview = Loadable(
  lazy(() => import('../pages/admin/membershipManagement/subscriptions/SubscriptionOverviewPage'))
);
const SpaceAdminDashboard = Loadable(lazy(() => import('../pages/admin/mealos-admin/SpaceAdminDashboard')));
const CafeteriaManagement = Loadable(lazy(() => import('../pages/admin/mealos-admin/CafeteriaManagement')));
const StaffAssignment = Loadable(lazy(() => import('../pages/admin/mealos-admin/StaffAssignment')));
const ShopManagement = Loadable(lazy(() => import('../pages/admin/shop-admin/ShopManagement')));
const InspectManagement = Loadable(lazy(() => import('../pages/admin/inspect-admin/InspectManagement')));
const InspectOverviewPage = Loadable(lazy(() => import('../pages/admin/inspect-admin/InspectOverviewPage')));
const InspectSessionsPage = Loadable(lazy(() => import('../pages/admin/inspect-admin/InspectSessionsPage')));
const InspectIssuesPage = Loadable(lazy(() => import('../pages/admin/inspect-admin/InspectIssuesPage')));
const InspectElectricityPage = Loadable(lazy(() => import('../pages/admin/inspect-admin/InspectElectricityPage')));

// Restaurant Admin
const RestaurantAdminDashboard = Loadable(lazy(() => import('../pages/restaurant-admin/RestaurantAdminDashboard')));
const MenuManagement = Loadable(lazy(() => import('../pages/restaurant-admin/MenuManagement')));
const MenuImportPage = Loadable(lazy(() => import('../pages/restaurant-admin/MenuImportPage')));
const RawMaterialImportPage = Loadable(lazy(() => import('../pages/restaurant-admin/RawMaterialImportPage')));
const RestaurantImportCenterPage = Loadable(lazy(() => import('../pages/restaurant-admin/ImportCenterPage')));
const OrdersManagement = Loadable(lazy(() => import('../pages/restaurant-admin/OrderManagement')));
const KitchenDisplaySystem = Loadable(lazy(() => import('../pages/restaurant-admin/KitchenDisplaySystem')));
const RestaurantInventoryManagement = Loadable(lazy(() => import('../pages/restaurant-admin/InventoryManagement')));
const InventoryLayout = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/InventoryLayout')));
const InventoryDashboardPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/InventoryDashboardPage')));
const InventoryStockPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/StockPage')));
const InventoryPurchasingPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/PurchasingPage')));
const InventoryGrnsPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/GrnsPage')));
const InventoryStockTakesPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/StockTakesPage')));
const InventoryPreparedPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/PreparedItemsPage')));
const InventoryVendorsPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/VendorsPage')));
const InventoryCategoriesPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/CategoriesPage')));
const InventoryReportsPage = Loadable(lazy(() => import('../pages/restaurant-admin/inventory/ReportsPage')));
const RazorpayIntegrationPage = Loadable(lazy(() => import('../pages/restaurant-admin/RazaorpayIntegrationPage')));
const RestaurantStaffAssignment = Loadable(lazy(() => import('../pages/restaurant-admin/StaffAssignment')));
const CafeSettings = Loadable(lazy(() => import('../pages/restaurant-admin/CafeteriaSettings')));
const CafePOSPage = Loadable(lazy(() => import('../pages/restaurant-admin/CafePOSPage')));
const CafeCouponsPage = Loadable(lazy(() => import('../pages/restaurant-admin/CafeteriaCouponsPage')));
const MealSchedulePage = Loadable(lazy(() => import('../pages/restaurant-admin/MealSchedulePage')));
const ChefPrinterSettingsPage = Loadable(lazy(() => import('../pages/restaurant-admin/ChefPrinterSettingsPage')));

// Public Lease
const LeaseSigningPage = Loadable(lazy(() => import('../pages/public/lease/LeaseSigningPage')));
// eslint-disable-next-line import/no-unresolved, import/extensions
const AddendumSigningPage = Loadable(lazy(() => import('../pages/public/lease/AddendumSigningPage')));
const AddendumOwnerSigningPage = Loadable(lazy(() => import('../pages/public/lease/AddendumOwnerSigningPage')));

const LeaseAdminSigningPage = Loadable(lazy(() => import('../pages/public/leaseAdmin/AdminSigningPage')));

// Public Virtual Office signing
const VOSigningPage = Loadable(lazy(() => import('../pages/public/virtualOffice/VOSigningPage')));
const VOAdminSigningPage = Loadable(lazy(() => import('../pages/public/virtualOffice/VOAdminSigningPage')));

const VisitorEntryHome = Loadable(lazy(() => import('../pages/public/visitor-management/Home')));


const PublicProposalPage = Loadable(lazy(() => import('../pages/public/proposal/PublicProposalPage')));

const PublicLeadFormPage = Loadable(lazy(() => import('../pages/public/leadForm/PublicLeadFormPage')));
const PublicTourPage = Loadable(lazy(() => import('../pages/public/tour/PublicTourPage')));
const TourManagePage = Loadable(lazy(() => import('../pages/public/tour/TourManagePage')));

const SignagePlayerPage = Loadable(lazy(() => import('../pages/public/signage/SignagePlayerPage')));
const SignagePairingPage = Loadable(lazy(() => import('../pages/public/signage/SignagePairingPage')));

// const KioskSettings = Loadable(lazy(() => import('../pages/public/kiosk/KioskSettings')));
// const VisitorEntryForm = Loadable(lazy(() => import('../pages/public/kiosk/VisitorEntry')));

const PaymentPage = Loadable(lazy(() => import('../pages/public/paymentRequest/PaymentPage')));

// const InvoiceManagement = Loadable(lazy(() => import('../pages/admin/financials/InvoiceManagement')));
// const ReceivablesManagement = Loadable(lazy(() => import('../pages/admin/financials/ReceivablesManagement')));
// const PendingBillables = Loadable(lazy(() => import('../pages/admin/financials/PendingBillables')));
// const Ledger = Loadable(lazy(() => import('../pages/admin/financials/Ledger')));
// const CreditDebitNotes = Loadable(lazy(() => import('../pages/admin/financials/CreditDebitNotes')));

const ManualEntryListPage = Loadable(
  lazy(() => import('../pages/admin/orderManagement/manualEntry/ManualEntryListPage'))
);
const ManualEntryCreatePage = Loadable(
  lazy(() => import('../pages/admin/orderManagement/manualEntry/ManualEntryCreatePage'))
);
// Staff attendance and payroll
const WorkforceDashboardPage = Loadable(lazy(() => import('../pages/admin/workforce/WorkforceDashboardPage')));
const AttendanceRegisterPage = Loadable(lazy(() => import('../pages/admin/workforce/AttendanceRegisterPage')));
const MusterPage = Loadable(lazy(() => import('../pages/admin/workforce/MusterPage')));
const StaffDirectoryPage = Loadable(lazy(() => import('../pages/admin/workforce/StaffDirectoryPage')));
const MyAttendancePage = Loadable(lazy(() => import('../pages/admin/workforce/MyAttendancePage')));
const LeavePage = Loadable(lazy(() => import('../pages/admin/workforce/LeavePage')));
const AttendanceRequestsPage = Loadable(lazy(() => import('../pages/admin/workforce/AttendanceRequestsPage')));
const RosterPlannerPage = Loadable(lazy(() => import('../pages/admin/workforce/RosterPlannerPage')));
const WorkforceSettingsPage = Loadable(lazy(() => import('../pages/admin/workforce/WorkforceSettingsPage')));
const WorkforceSetupWizard = Loadable(lazy(() => import('../pages/admin/workforce/WorkforceSetupWizard')));
const PayrollRunsPage = Loadable(lazy(() => import('../pages/admin/workforce/PayrollRunsPage')));
const PayrollRunDetailPage = Loadable(lazy(() => import('../pages/admin/workforce/PayrollRunDetailPage')));
const PayrollSettingsPage = Loadable(lazy(() => import('../pages/admin/workforce/PayrollSettingsPage')));
const PayrollAdvancesPage = Loadable(lazy(() => import('../pages/admin/workforce/PayrollAdvancesPage')));
const MyPayPage = Loadable(lazy(() => import('../pages/admin/workforce/MyPayPage')));
const WorkforceReportsPage = Loadable(lazy(() => import('../pages/admin/workforce/WorkforceReportsPage')));
const MyTeamPage = Loadable(lazy(() => import('../pages/admin/workforce/MyTeamPage')));
const OrderList = Loadable(lazy(() => import('../pages/admin/orderManagement/OrderList')));
const CreateSubscription = Loadable(lazy(() => import('../pages/admin/orderManagement/CreateSubscription')));
const CreateSubscriptionV2 = Loadable(lazy(() => import('../pages/admin/orderManagement/CreateSubscriptionV2')));
const BillingHandoffList = Loadable(lazy(() => import('../pages/admin/orderManagement/BillingHandoffList')));
const CreateVisitSubscription = Loadable(lazy(() => import('../pages/admin/orderManagement/CreateVisitSubscription')));
const CreatePlanSale = Loadable(lazy(() => import('../pages/admin/orderManagement/CreatePlanSale')));
const CreateMeetingRoomOrder = Loadable(lazy(() => import('../pages/admin/meetingRoom/CreateMeetingRoomOrder')));
const PaymentManagement = Loadable(
  lazy(() => import('../pages/admin/orderManagement/paymentManagement/PaymentManagement'))
);
const InvoiceList = Loadable(lazy(() => import('../pages/admin/orderManagement/invoice/InvoiceList')));

// Printing
const PrintingManagement = Loadable(lazy(() => import('../pages/admin/printing/PrintingManagement')));

const MeetingRoomCalendar = Loadable(lazy(() => import('../pages/admin/meetingRoom/MeetingRoomCalendarView')));
const CreateMeetingRoom = Loadable(lazy(() => import('../pages/admin/meetingRoom/CreateMeetingRoom')));
const EditMeetingRoom = Loadable(lazy(() => import('../pages/admin/meetingRoom/EditMeetingRoom')));
const MeetingRoomDashboard = Loadable(lazy(() => import('../pages/admin/meetingRoom/MeetingRoomDashboard')));
const ResourceTypes = Loadable(lazy(() => import('../pages/admin/meetingRoom/ResourceTypes')));
const AddMember = Loadable(lazy(() => import('../pages/admin/membershipManagement/AddMember')));
const CoWorkingDashboard = Loadable(lazy(() => import('../pages/admin/coworkingDashboard/Dashboard')));
const AdminDocumentation = Loadable(lazy(() => import('../pages/admin/documentation/DocumentationPage')));
const DeveloperSettingsPage = Loadable(lazy(() => import('../pages/admin/developer/DeveloperSettingsPage')));
const ApiKeyDetailPage = Loadable(lazy(() => import('../pages/admin/developer/ApiKeyDetailPage')));
const GuideLaunchPage = Loadable(lazy(() => import('../pages/admin/onboarding/GuideLaunchPage')));
const DexPage = Loadable(lazy(() => import('../pages/admin/dex/DexPage')));
const ExecutiveDashboard = Loadable(lazy(() => import('../pages/admin/coworkingDashboard/admin/ExecutiveDashboard')));
const AddOrgMembers = Loadable(lazy(() => import('../pages/admin/empManagement/Members')));

const ExpenseDetailPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseDetailPage')));
const ExpenseDashboard = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseDashboard')));
const ExpenseListPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseListPage')));
const ExpenseCategoriesPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseCategoriesPage')));
const ExpensesVendorsPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpensesVendorsPage')));
const ConsumablesHub = Loadable(lazy(() => import('../pages/admin/expenses/consumables/ConsumablesHub')));
const ConsumablesInventory = Loadable(lazy(() => import('../pages/admin/expenses/consumables/ConsumablesInventory')));
const ConsumablesConsumption = Loadable(lazy(() => import('../pages/admin/expenses/consumables/ConsumablesConsumption')));
const ConsumablesPurchases = Loadable(lazy(() => import('../pages/admin/expenses/consumables/ConsumablesPurchases')));
const ExpenseReportsPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseReportsPage')));
const RecurringExpensesPage = Loadable(lazy(() => import('../pages/admin/expenses/RecurringExpensesPage')));
const PettyCashPage = Loadable(lazy(() => import('../pages/admin/expenses/PettyCashPage')));
const ApprovalChainsPage = Loadable(lazy(() => import('../pages/admin/expenses/ApprovalChainsPage')));
const BudgetsPage = Loadable(lazy(() => import('../pages/admin/expenses/BudgetsPage')));
const ExpenseAccessPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseAccessPage')));
const ExpenseCapacityPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseCapacityPage')));
const ExpenseSettingsPage = Loadable(lazy(() => import('../pages/admin/expenses/ExpenseSettingsPage')));

const VisitorManagement = Loadable(lazy(() => import('../pages/admin/visitorManagement/VisitorManagement')));
const CourierManagement = Loadable(lazy(() => import('../pages/admin/courier/CourierManagement')));
const CourierSettings = Loadable(lazy(() => import('../pages/admin/courier/CourierSettings')));
const SignageManagement = Loadable(lazy(() => import('../pages/admin/signage/SignageManagement')));

// Compliance
const ComplianceDashboard = Loadable(lazy(() => import('../pages/admin/compliance/ComplianceDashboard')));

//  Lease Management
const LeaseManagementDashboard = Loadable(
  lazy(() => import('../pages/admin/leaseManagement/LeaseManagementDashboard'))
);
const LeaseList = Loadable(lazy(() => import('../pages/admin/leaseManagement/LeaseList')));
const LeaseEscalations = Loadable(lazy(() => import('../pages/admin/leaseManagement/EscalationsPage')));
const LeaseDetail = Loadable(lazy(() => import('../pages/admin/leaseManagement/LeaseDetail')));
const DepositsPage = Loadable(lazy(() => import('../pages/admin/leaseManagement/DepositsPage')));
const CreateLease = Loadable(lazy(() => import('../pages/admin/leaseManagement/CreateLease')));
const EditLease = Loadable(lazy(() => import('../pages/admin/leaseManagement/EditLease')));
const LeaseTnC = Loadable(lazy(() => import('../pages/admin/leaseManagement/TermsConditionsManagement')));
const LeaseSingnature = Loadable(lazy(() => import('../pages/admin/leaseManagement/SignatureManagement')));
const LeaseSettings = Loadable(lazy(() => import('../pages/admin/leaseManagement/LeaseSettings')));
const AddendumManagement = Loadable(lazy(() => import('../pages/admin/leaseManagement/AddendumManagement')));
const LeaseTemplateBuilder = Loadable(lazy(() => import('../pages/admin/leaseManagement/LeaseTemplateBuilder')));

// Virtual Office
const VirtualOfficeList = Loadable(lazy(() => import('../pages/admin/virtualOffice/VirtualOfficeList')));
const CreateVirtualOffice = Loadable(lazy(() => import('../pages/admin/virtualOffice/CreateVirtualOffice')));
const VirtualOfficeDetail = Loadable(lazy(() => import('../pages/admin/virtualOffice/VirtualOfficeDetail')));
const EditVirtualOffice = Loadable(lazy(() => import('../pages/admin/virtualOffice/EditVirtualOffice')));
const AmenityPackages = Loadable(lazy(() => import('../pages/admin/virtualOffice/AmenityPackages')));
const VOTemplateBuilder = Loadable(lazy(() => import('../pages/admin/virtualOffice/VOTemplateBuilder')));

const LeadsReportsPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/ReportsPage')));
const LeadsDashboardPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/LeadsDashboardPage')));
const OutreachDashboard = Loadable(lazy(() => import('../pages/admin/emailMarketing/EmailMarketingDashboard')));
const OutreachCampaigns = Loadable(lazy(() => import('../pages/admin/emailMarketing/campaigns/CampaignsPage')));
const OutreachCampaignDetail = Loadable(lazy(() => import('../pages/admin/emailMarketing/campaigns/CampaignDetailPage')));
const CampaignBuilderPage = Loadable(lazy(() => import('../pages/admin/emailMarketing/campaigns/CampaignBuilderPage')));
const AiCampaignWizardPage = Loadable(lazy(() => import('../pages/admin/emailMarketing/campaigns/AiCampaignWizardPage')));
const OutreachContacts = Loadable(lazy(() => import('../pages/admin/emailMarketing/contacts/ContactsPage')));
const OutreachContactsImportDeskos = Loadable(lazy(() => import('../pages/admin/emailMarketing/contacts/DeskosImportPage')));
const OutreachContactsImportCsv = Loadable(lazy(() => import('../pages/admin/emailMarketing/contacts/CsvImportPage')));
const OutreachContactAttributes = Loadable(lazy(() => import('../pages/admin/emailMarketing/contacts/ContactAttributesPage')));
const OutreachTemplates = Loadable(lazy(() => import('../pages/admin/emailMarketing/templates/TemplatesPage')));
const OutreachSettings = Loadable(lazy(() => import('../pages/admin/emailMarketing/settings/SenderSettingsPage')));
const LeadsList = Loadable(lazy(() => import('../pages/admin/leadsManagement/LeadsListPage')));
const MyDayPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/MyDayPage')));
const LeadsTeamPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/TeamPage')));
const LeadsTeamMemberDayPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/TeamMemberDayPage')));
const LeadDetailPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/LeadDetailPage')));
const LeadCompanyPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/CompanyPage')));
// const LeadsInvitationsPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/LeadsInvitationsPage'))); // Invitations not supported yet
const FollowUpsPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/FollowUpsPage')));
const LeadSettingsPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/LeadSettingsPage')));

const ProposalsList = Loadable(lazy(() => import('../pages/admin/leadsManagement/proposals/ProposalsListPage')));
const ProposalCreationPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/proposals/ProposalCreationPage')));
const ProposalSettingsPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/proposals/ProposalSettingsPage')));
const ProposalPDFTemplatePage = Loadable(lazy(() => import('../pages/admin/leadsManagement/proposals/ProposalPDFTemplatePage')));
const ProposalTemplateListPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/proposalTemplates/ProposalTemplateListPage')));
const ProposalTemplateBuilderPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/proposalTemplates/ProposalTemplateBuilderPage')));
const WebsiteHubPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/website/WebsiteHubPage')));
const LeadFormBuilderPage = Loadable(lazy(() => import('../pages/admin/leadsManagement/forms/FormBuilderPage')));

const CommunicationPage = Loadable(lazy(() => import('../pages/admin/communication/Communication')));

// Service Requests
const ServiceRequestsDashboard = Loadable(
  lazy(() => import('../pages/admin/serviceRequests/ServiceRequestsDashboard'))
);
const TicketCreationForm = Loadable(lazy(() => import('../pages/admin/serviceRequests/TicketCreationForm')));
const TicketsList = Loadable(lazy(() => import('../pages/admin/serviceRequests/Tickets')));
const TicketDetailPage = Loadable(lazy(() => import('../pages/admin/serviceRequests/TicketDetailPage')));
const WorkOrdersList = Loadable(lazy(() => import('../pages/admin/serviceRequests/workOrders/WorkOrdersList')));
const WorkOrderDetailPage = Loadable(
  lazy(() => import('../pages/admin/serviceRequests/workOrders/WorkOrderDetailPage'))
);
const WorkOrderTemplates = Loadable(
  lazy(() => import('../pages/admin/serviceRequests/workOrders/WorkOrderTemplates'))
);
const ServiceCategory = Loadable(lazy(() => import('../pages/admin/serviceRequests/settings/CategoriesPage')));
const ServiceTeams = Loadable(lazy(() => import('../pages/admin/serviceRequests/settings/TeamsPage')));
const ServiceRequestsSettingsPage = Loadable(
  lazy(() => import('../pages/admin/serviceRequests/settings/ServiceRequestsSettingsPage'))
);

// Receipts
const ReceiptManagement = Loadable(lazy(() => import('../pages/admin/orderManagement/receipt/ReceiptManagement')));
const CreateReceiptPage = Loadable(lazy(() => import('../pages/admin/orderManagement/receipt/CreateReceiptPage')));

// Credit Note
const CreditNoteList = Loadable(lazy(() => import('../pages/admin/orderManagement/creditNote/CreditNoteList')));
const CreateCreditNote = Loadable(lazy(() => import('../pages/admin/orderManagement/creditNote/CreateCreditNote')));

const BillToAccountManagement = Loadable(
  lazy(() => import('../pages/admin/orderManagement/billToAccount/BillToAccountManagement'))
);

const AdminDownloadPage = Loadable(lazy(() => import('../pages/admin/downloads/Downloads')));

const SettlementsPage = Loadable(lazy(() => import('../pages/admin/settlements/SettlementsPage')));
const DeskOSBillingPage = Loadable(lazy(() => import('../pages/admin/deskOSBilling/BillingPage')));
const DeskOSTrainingPage = Loadable(lazy(() => import('../pages/admin/deskosTraining/TrainingPage')));
const DeskOSSupportPage = Loadable(lazy(() => import('../pages/admin/deskosSupport/DeskOSSupportPage')));

const MonthlyBillingPage = Loadable(lazy(() => import('../pages/admin/accounting/MonthlyBillingPage')));
const InvoiceManagementPage = Loadable(lazy(() => import('../pages/admin/accounting/invoice/InvoiceManagement')));
const GSTTaxManagement = Loadable(lazy(() => import('../pages/admin/accounting/GSTTaxManagement')));

const AdminProfileSettings = Loadable(lazy(() => import('../pages/admin/profile/AdminProfileSettings')));
const AdminSettings = Loadable(lazy(() => import('../pages/admin/settings/AdminSettings')));
const RoleManagementPage = Loadable(lazy(() => import('../pages/admin/settings/roles/RoleManagementPage')));
const CollectionAccountsPage = Loadable(lazy(() => import('../pages/admin/settings/CollectionAccountsPage')));
const CollectionRegisterPage = Loadable(lazy(() => import('../pages/admin/orderManagement/CollectionRegisterPage')));
const InvoiceNumberManager = Loadable(lazy(() => import('../pages/admin/orderManagement/invoiceNumbers/InvoiceNumberManager')));
const AggregatorBookingsPage = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/AggregatorBookingsPage'))
);
const IntegrationsDashboard = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/IntegrationsDashboard'))
);
const WhatsAppSettingsPage = Loadable(
  lazy(() => import('../pages/admin/settings/whatsapp/WhatsAppSettingsPage'))
);
const WhatsAppMsg91Page = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/whatsapp/WhatsAppMsg91Page'))
);
const KnowlarityPage = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/telephony/KnowlarityPage'))
);
const NexudusPage = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/nexudus/NexudusPage'))
);
const NetworkPage = Loadable(lazy(() => import('../pages/admin/network')));
const CaptivePortalSetupPage = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/captiveportal/CaptivePortalSetupPage'))
);

const AICreditsPage = Loadable(lazy(() => import('../pages/admin/settings/AICreditsPage')));
const AIConnectionsPage = Loadable(lazy(() => import('../pages/admin/settings/AIConnectionsPage')));

const TallyConfigurationPage = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/tally/TallyConfigurationPage'))
);
const TallyCustomerMapping = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/tally/TallyCustomerMapping'))
);
const TallyExportPage = Loadable(lazy(() => import('../pages/admin/settings/integrations/tally/TallyExportPage')));
const AccountingIntegrationsPage = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/accounting/AccountingIntegrationsPage'))
);
const AccountingOAuthCallback = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/accounting/AccountingOAuthCallback'))
);
const TallyLedgerImportWizard = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/tally/TallyLedgerImportWizard'))
);
const TallyConnectPage = Loadable(lazy(() => import('../pages/admin/settings/integrations/tally/TallyConnectPage')));
const CompanyMappingDashboard = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/tally/CompanyMappingDashboard'))
);
const AutoMatchSuggestions = Loadable(
  lazy(() => import('../pages/admin/settings/integrations/tally/AutoMatchSuggestions'))
);

const ImportCenter = Loadable(lazy(() => import('../pages/admin/settings/dataImport/ImportCenter')));
const SpaceImportWizard = Loadable(lazy(() => import('../pages/admin/settings/dataImport/SpaceImportWizard')));
const LeaseImportWizard = Loadable(lazy(() => import('../pages/admin/settings/dataImport/LeaseImportWizard')));
const LeaseValidationPage = Loadable(lazy(() => import('../pages/admin/settings/dataImport/LeaseValidationPage')));
const BillingAddressImportPage = Loadable(lazy(() => import('../pages/admin/settings/dataImport/BillingAddressImportPage')));

const LateChargeManagement = Loadable(
  lazy(() => import('../pages/admin/orderManagement/invoice/lateCharges/LateChargeManagement'))
);

// Parking Management
const ParkingManagementPage = Loadable(lazy(() => import('../pages/admin/parking/ParkingManagementPage')));

// Asset Management
const AssetManagementPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetManagementPage')));
const AssetSettingsPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetSettingsPage')));
const AssetDetailPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetDetailPage')));
const MaintenancePage = Loadable(lazy(() => import('../pages/admin/assetManagement/MaintenancePage')));
const AMCPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AMCPage')));
const AssetInsurancePage = Loadable(lazy(() => import('../pages/admin/assetManagement/InsurancePage')));
const AssetVendorsPage = Loadable(lazy(() => import('../pages/admin/assetManagement/VendorsPage')));
const AssetFloorMapPage = Loadable(lazy(() => import('../pages/admin/assetManagement/FloorMapPage')));
const AssetAnalyticsPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AnalyticsDashboardPage')));
const AssetDamageReportsPage = Loadable(lazy(() => import('../pages/admin/assetManagement/DamageReportsPage')));
const AssetAssignmentsPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetAssignmentsPage')));
const AssetAuditsPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetAuditsPage')));
const AssetAuditRunPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetAuditRunPage')));
const AssetScanPage = Loadable(lazy(() => import('../pages/admin/assetManagement/AssetScanPage')));
const PublicAssetPage = Loadable(lazy(() => import('../pages/public/PublicAssetPage')));

// Meeting Room Displays
const MeetingRoomDisplaysPage = Loadable(lazy(() => import('../pages/admin/settings/MeetingRoomDisplays')));

// Access Control
const AccessControlDashboard = Loadable(lazy(() => import('../pages/admin/accessControl/AccessControlDashboard')));
const CardAssignmentPage = Loadable(lazy(() => import('../pages/admin/accessControl/CardAssignmentPage')));
const ZKBioSetupPage = Loadable(lazy(() => import('../pages/admin/accessControl/ZKBioSetupPage')));
const KisiSetupPage = Loadable(lazy(() => import('../pages/admin/accessControl/KisiSetupPage')));
const SpintlySaamsSetupPage = Loadable(lazy(() => import('../pages/admin/accessControl/SpintlySaamsSetupPage')));
const CodeUnlockSetupPage = Loadable(lazy(() => import('../pages/admin/accessControl/CodeUnlockSetupPage')));
const AccessEventLogsPage = Loadable(lazy(() => import('../pages/admin/accessControl/AccessEventLogsPage')));
const AccessInsightsPage = Loadable(lazy(() => import('../pages/admin/accessControl/AccessInsightsPage')));

// Reports
const ReportsDashboard = Loadable(lazy(() => import('../pages/admin/reports/ReportsDashboard')));

// Analytics (new BI module — semantic layer, builder, dashboards)
const AnalyticsHome = Loadable(lazy(() => import('../pages/admin/analytics/AnalyticsHome')));
const AnalyticsReportViewer = Loadable(lazy(() => import('../pages/admin/analytics/ReportViewer')));
const AnalyticsReportBuilder = Loadable(lazy(() => import('../pages/admin/analytics/ReportBuilder')));
const AnalyticsDashboardPage = Loadable(lazy(() => import('../pages/admin/analytics/DashboardPage')));

// Automations
const SystemAutomationsPage = Loadable(lazy(() => import('../pages/admin/automations/SystemAutomationsPage')));
const AutomationRunsPage = Loadable(lazy(() => import('../pages/admin/automations/AutomationRunsPage')));
const AutomationRulesPage = Loadable(lazy(() => import('../pages/admin/automations/AutomationRulesPage')));
const AutomationsPage = Loadable(lazy(() => import('../pages/admin/automations/AutomationsPage')));
const AutomationBuilderPage = Loadable(lazy(() => import('../pages/admin/automations/AutomationBuilderPage')));
// eslint-disable-next-line import/no-unresolved, import/extensions
const ChatbotDashboard = Loadable(lazy(() => import('../pages/admin/chatbots/ChatbotDashboard')));
const ChatbotsPage = Loadable(lazy(() => import('../pages/admin/chatbots/ChatbotsPage')));
const ChatbotBuilder = Loadable(lazy(() => import('../pages/admin/chatbots/ChatbotBuilder')));
const ChatbotInbox = Loadable(lazy(() => import('../pages/admin/chatbots/ChatbotInbox')));
const ChatbotKnowledge = Loadable(lazy(() => import('../pages/admin/chatbots/ChatbotKnowledge')));
const ChatbotAiPage = Loadable(lazy(() => import('../pages/admin/chatbots/ChatbotAiPage')));

// Admin Notifications
const AdminNotificationsPage = Loadable(lazy(() => import('../pages/admin/notifications/NotificationsPage')));

// Sync
const SyncDashboard = Loadable(lazy(() => import('../pages/admin/sync/SyncDashboard')));
const TallyDash = Loadable(lazy(() => import('../pages/admin/sync/pages/TallyDash')));

const TallyMappingDashboard = Loadable(lazy(() => import('../pages/admin/sync/pages/tally/TallyMappingDashboard')));

const AdminLogin = Loadable(lazy(() => import('../pages/admin/auth/AdminLogin')));

//  Check In Kiosk
const CheckInScanner = Loadable(lazy(() => import('../pages/cik/Scanner')));
const CheckoutScanner = Loadable(lazy(() => import('../pages/cik/CheckoutScanner')));
const CIKHome = Loadable(lazy(() => import('../pages/cik/Home')));

const KioskLogin = Loadable(lazy(() => import('../pages/cik/auth/KioskLogin')));

// OTP Login for User
const UserLogin = Loadable(lazy(() => import('../pages/user/auth/UserLogin')));
const PublicPlans = Loadable(lazy(() => import('../pages/user/auth/PublicPlansPage')));
const LandingPage = Loadable(lazy(() => import('../pages/user/auth/LandingPage')));

// Pending Payments
const PaymentReqDetail = Loadable(lazy(() => import('../pages/user/pending-payments/PaymentDetail')));
const PaymentReqSuccess = Loadable(lazy(() => import('../pages/user/pending-payments/PaymentSuccess')));

// Boss
const BuildMyWork = Loadable(lazy(() => import('../pages/deskos/build/MyWorkPage')));
const BuildBoard = Loadable(lazy(() => import('../pages/deskos/build/SprintBoardPage')));
const BuildBacklog = Loadable(lazy(() => import('../pages/deskos/build/BacklogPage')));
const BuildSprints = Loadable(lazy(() => import('../pages/deskos/build/SprintsPage')));
const BuildDeploys = Loadable(lazy(() => import('../pages/deskos/build/DeployRequestsPage')));
const BuildDeployDetail = Loadable(lazy(() => import('../pages/deskos/build/DeployRequestDetail')));
const BuildQueue = Loadable(lazy(() => import('../pages/deskos/build/DeployQueuePage')));
const BuildRepos = Loadable(lazy(() => import('../pages/deskos/build/ReposPage')));
const BossOrderList = Loadable(lazy(() => import('../pages/deskos/order/OrderList')));
const BossOrderDetail = Loadable(lazy(() => import('../pages/deskos/order/OrderDetail')));
const BossCreateOrder = Loadable(lazy(() => import('../pages/deskos/order/CreateOrder')));
const BossCreateClubbedOrder = Loadable(lazy(() => import('../pages/deskos/order/CreateClubbedOrder')));
const BossMonthlyBilling = Loadable(lazy(() => import('../pages/admin/accounting/MonthlyBillingPage')));
const BossEmailSettings = Loadable(lazy(() => import('../pages/deskos/settings/DeskOSEmailSettingsPage')));
const BossSupportConfig = Loadable(lazy(() => import('../pages/deskos/support/SupportConfigPage')));
const BossWhatsAppConsole = Loadable(lazy(() => import('../pages/deskos/whatsapp/WhatsAppConsolePage')));
const BossPrintlyAccess = Loadable(
  lazy(() => import('../pages/deskos/printly/PrintlyAccessCodesPage'))
);
const BossPrintlyReleases = Loadable(
  lazy(() => import('../pages/deskos/printly/PrintlyReleasesPage'))
);
const BossAICredits = Loadable(lazy(() => import('../pages/deskos/credits/AICreditsConsolePage')));
const BossStore = Loadable(lazy(() => import('../pages/deskos/store/StoreConsolePage')));
const BossAdoption = Loadable(lazy(() => import('../pages/deskos/adoption/AdoptionPage')));
const BossTrainingRequests = Loadable(lazy(() => import('../pages/deskos/training/TrainingRequestsPage')));
const BossTrainingSessions = Loadable(lazy(() => import('../pages/deskos/training/TrainingSessionsPage')));
const BossTrainingSettings = Loadable(lazy(() => import('../pages/deskos/training/TrainingSettingsPage')));
const BossSupportTickets = Loadable(lazy(() => import('../pages/deskos/support/SupportTicketsPage')));
const BossSupportTicketDetail = Loadable(lazy(() => import('../pages/deskos/support/SupportTicketDetailPage')));
const BossOrgOnboarding = Loadable(lazy(() => import('../pages/deskos/onboarding/OrgOnboardingPage')));
const BossManagerOrgList = Loadable(lazy(() => import('../pages/deskos/manager/ManagerOrgListPage')));
const BossManagerOrgDetail = Loadable(lazy(() => import('../pages/deskos/manager/ManagerOrgDetailPage')));
const CrmMyDayPage = Loadable(lazy(() => import('../pages/deskos/crm/CrmMyDayPage')));
const CrmSalesDashboard = Loadable(lazy(() => import('../pages/deskos/crm/CrmSalesDashboard')));
const CrmAccountsPage = Loadable(lazy(() => import('../pages/deskos/crm/CrmAccountsPage')));
const CrmAccountDetail = Loadable(lazy(() => import('../pages/deskos/crm/CrmAccountDetail')));
const CrmActivitiesPage = Loadable(lazy(() => import('../pages/deskos/crm/CrmActivitiesPage')));
const CrmContactsPage = Loadable(lazy(() => import('../pages/deskos/crm/CrmContactsPage')));
const CrmTeamDashboard = Loadable(lazy(() => import('../pages/deskos/crm/CrmTeamDashboard')));
const CrmConnectionsPage = Loadable(lazy(() => import('../pages/deskos/crm/CrmConnectionsPage')));

// deskos.net content (the marketing CMS)
const WebsitePostsPage = Loadable(lazy(() => import('../pages/deskos/website/WebsitePostsPage')));
const WebsitePostEditor = Loadable(lazy(() => import('../pages/deskos/website/WebsitePostEditor')));
const WebsiteTaxonomyPage = Loadable(lazy(() => import('../pages/deskos/website/WebsiteTaxonomyPage')));
const WebsiteMediaPage = Loadable(lazy(() => import('../pages/deskos/website/WebsiteMediaPage')));

// Public Meeting Room
const PublicMeetingRoom = Loadable(lazy(() => import('../pages/public/PublicMeetingRoom')));
const PublicMeetingRoomDirectory = Loadable(lazy(() => import('../pages/public/PublicMeetingRoomDirectory')));

// Self-serve signup (coworking/office-space admins)
const SignupPage = Loadable(lazy(() => import('../pages/signup/SignupPage')));
const SignupOnboardingWizard = Loadable(lazy(() => import('../pages/signup/wizard/OnboardingWizard')));

// Public Day Pass
const PublicDayPass = Loadable(lazy(() => import('../pages/public/PublicDayPass')));
const JoinCompany = Loadable(lazy(() => import('../pages/public/JoinCompany')));

// WhatsApp Embedded Signup. Served from the canonical connect.deskos.net origin
// because Meta's SDK domain allowlist cannot cover every whitelabel subdomain.
const WhatsAppConnect = Loadable(lazy(() => import('../pages/public/WhatsAppConnect')));


// Public Meeting Room Credits
const PublicMeetingRoomCredits = Loadable(lazy(() => import('../pages/public/PublicMeetingRoomCredits')));

// Meeting Room Credit Plans (admin)

// Meeting Room Credits (user purchase)
const PlanStorePage = Loadable(lazy(() => import('../pages/user/plans/PlanStorePage')));

// #########################

// GENERAL
const Crossing = Loadable(lazy(() => import('../pages/auth/Crossing')));

const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

const WrongSubdomainPage = Loadable(lazy(() => import('../pages/WrongSubdomainPage')));

const ManualEntryViewPage = Loadable(
  lazy(() => import('../pages/admin/orderManagement/manualEntry/ManualEntryViewPage'))
);
const ManualEntryEditPage = Loadable(
  lazy(() => import('../pages/admin/orderManagement/manualEntry/ManualEntryEditPage'))
);

// ── ATLAS — Sales Tablet ───────────────────────────────────────────────────────
const LiveInventoryLogin = Loadable(lazy(() => import('../pages/live-inventory/LiveInventoryLogin')));
const LiveInventorySetPassword = Loadable(lazy(() => import('../pages/live-inventory/LiveInventorySetPassword')));
const LiveInventoryPage = Loadable(lazy(() => import('../pages/live-inventory/LiveInventoryPage')));
const LiveInventoryPublicPage = Loadable(lazy(() => import('../pages/live-inventory/LiveInventoryPublicPage')));
const AtlasLoginPage = Loadable(lazy(() => import('../pages/atlas/AtlasLoginPage')));
const InventoryBoardPage = Loadable(lazy(() => import('../pages/atlas/InventoryBoardPage')));
const SpaceDetailPage = Loadable(lazy(() => import('../pages/atlas/SpaceDetailPage')));
// eslint-disable-next-line import/no-unresolved
const AtlasDashboardPage = Loadable(lazy(() => import('../pages/atlas/AtlasDashboardPage')));
const AtlasLeadsPage = Loadable(lazy(() => import('../pages/atlas/AtlasLeadsPage')));
const AtlasLeadFormPage = Loadable(lazy(() => import('../pages/atlas/AtlasLeadFormPage')));
const AtlasLeadDetailPage = Loadable(lazy(() => import('../pages/atlas/AtlasLeadDetailPage')));
const AtlasProposalsPage = Loadable(lazy(() => import('../pages/atlas/AtlasProposalsPage')));
const AtlasProposalCreatePage = Loadable(lazy(() => import('../pages/atlas/AtlasProposalCreatePage')));
const AtlasProposalDetailPage = Loadable(lazy(() => import('../pages/atlas/AtlasProposalDetailPage')));
// eslint-disable-next-line import/no-unresolved
const AtlasCalendarPage = Loadable(lazy(() => import('../pages/atlas/AtlasCalendarPage')));